import { useState, useEffect, useRef, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { DELETE_CART_PAGE, MY_CAMPAIGN_LIST_QUERY } from '../Graphql/Queries';
import { GET_PACKAGE_DETAILS } from '@/Graphql/packageQurie';
import client, { MAPBOX_TOKEN } from '../Graphql/apolloClient';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';


interface Campaign {
  currencyCode: string
  id: string;
  name: string;
  code: string;
  dealId: string;
  duration: string;
  inventories: number;
  status: string;
  impressions: number;
  totalPrice: number;
  bookings?: any[];
  startDate: { dateStr: string };
  endDate: { dateStr: string };
  campaignInventories?: Array<{
    inventoryType: string;
    id: string;
    inventoryId: string;
    inventoryName: string;
    inventoryThumbnailUrl: string;
    inventoryAddress: string;
    inventoryReferenceId: string;
    inventoryResolution: string;
    inventoryVenueType: string;
    inventoryLatitude?: number;
    inventoryLongitude?: number;
    inventoryReports: { totalPotentialViews: number };
    reasonForRejection: string;
    nowPayPrice: number | null;
    mediaOwnerName: string;
  }>;
  campaignStatus: string;
  summaryReport: { potentialViews: number };
  priceSummary: {
    netTotal: number;
    currency: { code: string; symbol: string };
  };
  reasonForRejection: string;
  reporting: {
    proofOfPlay: boolean;
    deliveryReports: boolean;
  };
}
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Search, Filter, Plus, Eye, MoreHorizontal, FileText, Play, TrendingUp, CreditCard, ChevronLeft, ChevronRight, ChevronDown, Settings, ArrowRight, Clock, MapPin, Image as ImageIcon, XCircle, AlertTriangle, X, Calendar, AlertCircle } from "lucide-react";

import { useNavigate, Link } from "react-router-dom";
import { useCart } from "@/utils/cartContext";
import { useToast } from "@/hooks/use-toast";
import { generateMockData } from "@/Graphql/MockData";
import type { Configuration } from "@/types/config";
import { GET_MY_CART_ITEMS } from "@/Graphql/cartQueries";

type BookingData = {
  id: number;
  name: string;
  price: number;
  location: string;
  impressions: number;
  daypartSchedules?: string[];
  type?: string;
  code?: string;
  siteLocation?: string;
  category?: string;
  specifications?: {
    resolution?: string;
    [key: string]: any;
  };
  mainImage?: string;
  galleryImages?: string[];
};

interface CampaignsProps {
  base_path?: string;
  yourLogo?: string;
  // Removed currencyCode prop
  configData?: any[];
}

// Constants for image arrays to improve performance
const MAIN_IMAGES = [
  'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop&auto=format',
  'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop&auto=format',
  'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop&auto=format',
  'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop&auto=format'
];

const THUMBNAIL_IMAGES = [
  'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=60&h=60&fit=crop&auto=format',
  'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=60&h=60&fit=crop&auto=format',
  'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=60&h=60&fit=crop&auto=format',
  'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=60&h=60&fit=crop&auto=format'
];

// Helper function to handle booking item click
const handleBookingClick = (originalIndex: number, setSelectedInventoryIndex: (index: number) => void, setSelectedMainImage: (image: string) => void) => {
  setSelectedInventoryIndex(originalIndex);
  setSelectedMainImage(MAIN_IMAGES[originalIndex % MAIN_IMAGES.length] || MAIN_IMAGES[0]);
};

// Helper function to get thumbnail image
const getThumbnailImage = (originalIndex: number) => {
  return THUMBNAIL_IMAGES[originalIndex % THUMBNAIL_IMAGES.length] || THUMBNAIL_IMAGES[0];
};

// Helper function for inventory type colors
const getInventoryTypeColor = (type: string) => {
  switch (type) {
    case 'Digital': return { border: 'border-l-blue-500', bg: 'bg-blue-50', text: 'text-blue-700', badge: 'bg-blue-100 text-blue-800' };
    case 'Classic': return { border: 'border-l-green-500', bg: 'bg-green-50', text: 'text-green-700', badge: 'bg-green-100 text-green-800' };
    case 'Package': return { border: 'border-l-purple-500', bg: 'bg-purple-50', text: 'text-purple-700', badge: 'bg-purple-100 text-purple-800' };
    case 'Transit': return { border: 'border-l-orange-500', bg: 'bg-orange-50', text: 'text-orange-700', badge: 'bg-orange-100 text-orange-800' };
    default: return { border: 'border-l-gray-500', bg: 'bg-gray-50', text: 'text-gray-700', badge: 'bg-gray-100 text-gray-800' };
  }
};

// Convert campaign inventories to billboard format for MapView component (adapted from promotional)
const convertInventoriesToBillboards = (inventories: any[]) => {
  return inventories.map((inventory, index) => ({
    id: inventory.inventoryId || inventory.id || index.toString(),
    name: inventory.inventoryName || inventory.name || `Billboard ${index + 1}`,
    thumbnailUrl: inventory.inventoryThumbnailUrl || THUMBNAIL_IMAGES[index % THUMBNAIL_IMAGES.length],
    referenceId: inventory.inventoryReferenceId || inventory.code || `REF${index + 1}`,
    address: inventory.inventoryAddress || inventory.location || 'N/A',
    resolution: inventory.inventoryResolution || inventory.specifications?.resolution || 'N/A',
    venueType: inventory.inventoryVenueType || inventory.category || 'Standard',
    latitude: inventory.inventoryLatitude || inventory.lat || -37.8136 + (Math.random() - 0.5) * 0.1,
    longitude: inventory.inventoryLongitude || inventory.lng || 144.9631 + (Math.random() - 0.5) * 0.1,
    impressions: inventory.inventoryReports?.totalPotentialViews || inventory.impressions || Math.floor(Math.random() * 100000) + 10000,
    price: inventory.nowPayPrice || inventory.price || Math.floor(Math.random() * 5000) + 1000,
    type: inventory.inventoryType || inventory.type || 'Digital',
    image: inventory.mainImage || MAIN_IMAGES[index % MAIN_IMAGES.length],
    galleryImages: inventory.galleryImages || [MAIN_IMAGES[index % MAIN_IMAGES.length]]
  }));
};

const _inventoryTypeDisplayMapping: { [key: string]: string } = {
  'digital': 'Digital',
  'digital_billboard': 'Digital',
  'classic': 'Classic',
  'static': 'Classic',
  'static_billboard': 'Classic',
  'deal': 'Deal'
};

export default function Campaigns({ base_path, configData }: CampaignsProps) {

  const [deleteItems, setDeleteItems] = useState<any[]>([]);
  const [showDeleteAllModal, setShowDeleteAllModal] = useState(false);



  const navigateRouter = useNavigate();
  const { addToCart, cartItems, clearCart, cartCount } = useCart();
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [appliedSearchTerm, setAppliedSearchTerm] = useState("");
  const [searchError, setSearchError] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [filteredCampaigns, setFilteredCampaigns] = useState<Campaign[]>([]);
  const [totalElements, setTotalElements] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const campaignsPerPage = 10;
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [visibleColumns, setVisibleColumns] = useState({
    campaign: true,
    duration: true,
    inventories: true,
    status: true,
    impressions: true,
    totalPrice: true,
    bookings: true,
    action: true
  });

  // Add CSS to ensure white backgrounds for dropdowns
  useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      [data-radix-select-content] {
        background-color: white !important;
      }
      [data-radix-dropdown-menu-content] {
        background-color: white !important;
      }
      [data-radix-select-item] {
        background-color: white !important;
      }
      [data-radix-select-item]:hover {
        background-color: #f9fafb !important;
      }
      [data-radix-dropdown-menu-item]:hover {
        background-color: #f9fafb !important;
      }
    `;
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  // Modal state for booking show
  const [showBookingModal, setShowBookingModal] = useState(false);
  const [selectedCampaignBookings, setSelectedCampaignBookings] = useState<any>(null);
  const [activeTab, setActiveTab] = useState("map");
  const [selectedMainImage, setSelectedMainImage] = useState<string>("");
  const [selectedInventoryIndex, setSelectedInventoryIndex] = useState<number>(0);
  const [modalSearchTerm, setModalSearchTerm] = useState("");

  // Billboard data for map integration (adapted from promotional)
  const [billboardsForMap, setBillboardsForMap] = useState<any[]>([]);
  const [selectedInventoryForMap, setSelectedInventoryForMap] = useState<any>(null);

  // Map state variables for Mapbox integration
  const [map, setMap] = useState<mapboxgl.Map | null>(null);
  const [marker, setMarker] = useState<mapboxgl.Marker | null>(null);
  const [currentPopup, setCurrentPopup] = useState<mapboxgl.Popup | null>(null);
  const [mapForceRender, setMapForceRender] = useState<number>(0);
  const mapContainer = useRef<HTMLDivElement>(null);

  // Cart conflict modal state
  const [showCartConflictModal, setShowCartConflictModal] = useState(false);
  const [pendingDraftCampaign, setPendingDraftCampaign] = useState<any>(null);

  // Package state for package inventories
  const [packageDetails, setPackageDetails] = useState<any>(null);
  const [isLoadingPackage, setIsLoadingPackage] = useState(false);
  const [packageError, setPackageError] = useState<string | null>(null);

  // State for tracking which packages are expanded and their individual details
  const [expandedPackages, setExpandedPackages] = useState<Set<string>>(new Set());
  const [individualPackageDetails, setIndividualPackageDetails] = useState<Map<string, any>>(new Map());
  const [loadingPackages, setLoadingPackages] = useState<Set<string>>(new Set());

  // Toggle package expansion
  const togglePackageExpansion = async (packageId: string, packageItem: any) => {
    const newExpandedPackages = new Set(expandedPackages);

    if (expandedPackages.has(packageId)) {
      // Collapse package
      newExpandedPackages.delete(packageId);
    } else {
      // Expand package - fetch details if not already loaded
      newExpandedPackages.add(packageId);

      if (!individualPackageDetails.has(packageId)) {
        await fetchIndividualPackageDetails(packageId);
      }
    }

    setExpandedPackages(newExpandedPackages);
  };

  // Handle modal close - reset package state
  const handleModalClose = (open: boolean) => {
    if (!open) {
      // Reset package state when modal is closed
      setPackageDetails(null);
      setIsLoadingPackage(false);
      setPackageError(null);
      setExpandedPackages(new Set()); // Reset expanded packages
      setIndividualPackageDetails(new Map()); // Reset individual package details
      setLoadingPackages(new Set()); // Reset loading packages
    }
    setShowBookingModal(open);
  };

  // Handler to close Cart Conflict Modal and open Delete All Modal
  const handleClearCartAndContinue = async () => {
    setShowCartConflictModal(false);
    await handleClearCartButton();
    setShowDeleteAllModal(true);
  };

  // Clear Cart button logic (API deletion)
  // Fetch latest cart items from API before clearing
  const handleClearCartButton = async () => {
    const token = localStorage.getItem("authToken");
    const userId = localStorage.getItem("userId");
    if (!token || !userId) return;

    try {
      // Replace with your cart API query
      const { data } = await client.query({
        query: GET_MY_CART_ITEMS, // Use your actual cart query
        variables: {
          accessToken: token,
          userId: userId,
        },
        fetchPolicy: "network-only"
      });
      const apiCartItems = data?.cartpageItem || [];
      // Prepare items for deletion
      const itemsToDelete = apiCartItems.map((item: any) => ({
        id: item.id,
        itemType: "cart-data"
      }));
      setDeleteItems(itemsToDelete);
      setShowDeleteAllModal(true);
      // toast({ title: "Confirmation", description: "Are you sure you want to clear the cart?", variant: "default" });
    } catch (error) {
      toast({ title: "Error", description: "Failed to fetch cart items.", variant: "destructive" });
    }
  };

  // Confirm delete all (API call)
  const confirmDeleteAll = async () => {
    const token = localStorage.getItem("authToken");
    if (!token || !deleteItems.length) return;

    try {
      const response = await client.mutate({
        mutation: DELETE_CART_PAGE,
        variables: {
          accessToken: token,
          deleteItems: deleteItems
        },
        fetchPolicy: "no-cache",
      });
      const success = response?.data?.deleteCartItem?.success;
      const message = response?.data?.deleteCartItem?.message || (success ? "Cart cleared successfully" : "Failed to clear cart");
      if (success) {
        toast({ title: "Cart Cleared", description: message, variant: "default" });
        clearCart();
        if (pendingDraftCampaign) {
          console.log("redirecting to cart page");

          loadDraftToCart(pendingDraftCampaign);
          setPendingDraftCampaign(null);
        }
      } else {
        toast({ title: "Error", description: message, variant: "destructive" });
      }
    } catch (error) {
      toast({ title: "Error", description: "Failed to clear cart.", variant: "destructive" });
    } finally {
      setShowDeleteAllModal(false);
      setDeleteItems([]);
    }
  };

  // Save cart as draft modal state
  const [showSaveDraftModal, setShowSaveDraftModal] = useState(false);
  const [draftCampaignName, setDraftCampaignName] = useState("");
  const [draftStartDate, setDraftStartDate] = useState("");
  const [draftEndDate, setDraftEndDate] = useState("");
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [config, setConfig] = useState<Configuration[]>([]);
  const [reportConfig, setReportConfig] = useState<Configuration[]>([]);
  // Campaign rejection modal state (handles both cancellation and rejection)
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selectedCampaignToReject, setSelectedCampaignToReject] = useState<any>(null);
  const [rejectionReason, setRejectionReason] = useState("");
  const [rejectionReasonDetails, setRejectionReasonDetails] = useState("");
  const [isProcessingRejection, setIsProcessingRejection] = useState(false);
  const [rejectionType, setRejectionType] = useState<"buyer_cancel" | "media_owner_reject">("buyer_cancel");
  // Campaign rejection reason display modal
  const [showRejectionReasonModal, setShowRejectionReasonModal] = useState(false);
  const [selectedRejectionReason, setSelectedRejectionReason] = useState("");

  // Status color mapping for campaign badges
  const statusColors = {
    "Draft": "bg-gray-100 text-gray-800",
    "GENERATED": "bg-gray-100 text-gray-800",
    "Requested": "bg-emerald-100 text-emerald-800",
    "REQUESTED": "bg-emerald-100 text-emerald-800",
    "Pending Payment": "bg-yellow-100 text-yellow-800",
    "PAYMENT_PENDING": "bg-yellow-100 text-yellow-800",
    "Approved": "bg-green-100 text-green-800",
    "APPROVED": "bg-green-100 text-green-800",
    "Rejected": "bg-red-100 text-red-800",
    "REJECTED": "bg-red-100 text-red-800",
    "Live": "bg-blue-100 text-blue-800",
    "LIVE": "bg-blue-100 text-blue-800",
    "Completed": "bg-purple-100 text-purple-800",
    "COMPLETED": "bg-purple-100 text-purple-800",
    "Re-opened": "bg-orange-100 text-orange-800",
    "RE_OPENED": "bg-orange-100 text-orange-800"
  };

  // Fetch campaigns on component mount and when filters change
  useEffect(() => {
    // Clear campaigns if search term is less than 3 characters but not empty
    if (appliedSearchTerm && appliedSearchTerm.length < 3) {
      setFilteredCampaigns([]);
      setTotalElements(0);
    } else {
      fetchCampaignDetails();
    }
  }, [appliedSearchTerm, statusFilter, currentPage]);

  // Handle search functionality (Enter key based, not debounced)
  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setSearchQuery(value);
    setSearchTerm(value);
    setSearchError('');

    // If search is cleared, show all campaigns
    if (!value.trim()) {
      setAppliedSearchTerm('');
      setCurrentPage(1);
    }
  };

  useEffect(() => {
    const fetchConfig = async () => {
      try {
        if (configData) {
          const ordersConfig = configData.find((page) => page.name === 'My orders');
          const cartConfig = configData.find((page) => page.name === 'Cart');
          const reportsConfig = configData.find((page) => page.name === 'Reports');

          if (!ordersConfig || !cartConfig || !reportsConfig) {
            console.warn('Missing configuration data');
            return;
          }

          setConfig(ordersConfig.configurations || []);
          setReportConfig(reportsConfig.configurations || []);
        }
      } catch (error) {
        console.error('Error processing config:', error);
      }
    };
    fetchConfig();
  }, [configData]);

  const isFieldEnabled = (key: string) => {
    const fieldConfig = config.find((field) => field.key === key);
    return fieldConfig ? fieldConfig.default === 'yes' || fieldConfig.default === true : true;
  };


  const handleSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const trimmed = searchTerm.trim();

      // Check for minimum 3 characters
      if (trimmed.length > 0 && trimmed.length < 3) {
        setSearchError('Search term must be at least 3 characters long.');
        return;
      }

      // Allow search for empty string (clear search) or 3+ characters
      if (trimmed.length === 0 || trimmed.length >= 3) {
        setAppliedSearchTerm(trimmed);
        setCurrentPage(1);
        setSearchError('');
      } else {
        setSearchError('Please enter a search term.');
      }
    }
  };

  const handleResetSearch = () => {
    setSearchQuery('');
    setSearchTerm('');
    setAppliedSearchTerm('');
    setSearchError('');
    setCurrentPage(1);
  };

  // Helper function to check if campaign can be rejected (48-hour rule for buyer cancellation)
  const canRejectCampaign = (campaign: any) => {
    // Only Approved campaigns can be rejected - Live campaigns cannot be rejected
    if (campaign.status !== "Approved") {
      return false;
    }

    // Parse start date from duration string or use startDate field
    let startDate: Date;
    if (campaign.startDate) {
      startDate = new Date(campaign.startDate);
    } else {
      // Parse from duration string like "01 Mar 2024 - 31 Mar 2024"
      const startDateStr = campaign.duration.split(" - ")[0];
      startDate = new Date(startDateStr);
    }

    if (isNaN(startDate.getTime())) {
      return false; // Invalid date
    }

    const now = new Date();
    const timeDiff = startDate.getTime() - now.getTime();
    const hoursDiff = timeDiff / (1000 * 3600);

    // Can reject if start date is more than 48 hours away
    return hoursDiff > 48;
  };

  // Function to get action items based on campaign status
  const getActionItems = (status: string, campaign?: Campaign) => {
    switch (status) {
      case "GENERATED":
      case "Draft":
        return [
          { icon: ArrowRight, label: "Continue", action: "continue", color: "text-emerald-600" }
        ];
      case "REQUESTED":
      case "Requested":
        return []; // No actions for requested campaigns (pending approval)
      case "PAYMENT_PENDING":
      case "Pending Payment":
        return [
          { icon: CreditCard, label: "Pay Now", action: "pay", color: "text-emerald-600" }
        ];
      case "APPROVED":
      case "Approved":
        const actions = [];
        if (isFieldEnabled('creative')) {
          actions.push({
            icon: FileText,
            label: "Creative",
            action: "creative",
            color: "text-emerald-600"
          });
        }
        if (isFieldEnabled('paymentInvoice')) {
          actions.push({
            icon: CreditCard,
            label: "Payment Invoice",
            action: "invoice",
            color: "text-emerald-600"
          });
        }
        return actions;
      case "LIVE":
      case "Live":
        const liveActions = [];
        if (isFieldEnabled('creative')) {
          liveActions.push({
            icon: FileText,
            label: "Creative",
            action: "creative",
            color: "text-emerald-600"
          });
        }
        // Check and log reporting status for Proof of Play
        console.log('Campaign reporting status:', {
          campaignId: campaign?.id,
          campaignName: campaign?.name,
          proofOfPlay: campaign?.reporting?.proofOfPlay,
          deliveryReports: campaign?.reporting?.deliveryReports
        });

        if (campaign?.reporting?.proofOfPlay === true) {
          console.log('Adding Proof of Play action for campaign:', campaign.id);
          liveActions.push({
            icon: Play,
            label: "Proof of Play",
            action: "proof",
            color: "text-emerald-600"
          });
        }

        // Check both config and reporting status for Delivery Report
        const deliveryReportEnabled = isFieldEnabled('deliveryReport');
        const hasDeliveryReports = campaign?.reporting?.deliveryReports === true;

        console.log('Delivery Report status:', {
          campaignId: campaign?.id,
          configEnabled: deliveryReportEnabled,
          reportingEnabled: hasDeliveryReports
        });

        if (deliveryReportEnabled && hasDeliveryReports) {
          console.log('Adding Delivery Report action for campaign:', campaign.id);
          liveActions.push({
            icon: TrendingUp,
            label: "Delivery Report",
            action: "delivery",
            color: "text-emerald-600"
          });
        }

        if (isFieldEnabled('paymentInvoice')) {
          liveActions.push({
            icon: CreditCard,
            label: "Payment Invoice",
            action: "invoice",
            color: "text-emerald-600"
          });
        }
        return liveActions;
      case "COMPLETED":
      case "Completed":
        const completedActions = [];
        if (isFieldEnabled('creative')) {
          completedActions.push({
            icon: FileText,
            label: "Creative",
            action: "creative",
            color: "text-emerald-600"
          });
        }
        // Add Proof of Play action only if enabled in reporting
        if (campaign?.reporting?.proofOfPlay) {
          completedActions.push({
            icon: Play,
            label: "Proof of Play",
            action: "proof",
            color: "text-emerald-600"
          });
        }
        // Add Delivery Report action only if enabled in both config and reporting
        if (isFieldEnabled('deliveryReport') && campaign?.reporting?.deliveryReports) {
          completedActions.push({
            icon: TrendingUp,
            label: "Delivery Report",
            action: "delivery",
            color: "text-emerald-600"
          });
        }
        if (isFieldEnabled('paymentInvoice')) {
          completedActions.push({
            icon: CreditCard,
            label: "Payment Invoice",
            action: "invoice",
            color: "text-emerald-600"
          });
        }
        return completedActions;
      case "REJECTED":
      case "Rejected":
        return [
          { icon: AlertCircle, label: "View Reason", action: "view_reason", color: "text-red-600" }
        ];
      default:
        return [];
    }
  };

  // GraphQL fetch function
  const fetchCampaignDetails = async () => {
    if (import.meta.env.VITE_APP_MOCK_DATA === 'true') {
      const mockResponse = await generateMockData(MY_CAMPAIGN_LIST_QUERY);

      const campaigns = mockResponse.data.mycampaignList.content || [];
      const totalElements = mockResponse.data.mycampaignList.totalElements || campaigns.length;

      // Transform API data to match your UI structure
      const transformedCampaigns = campaigns.map((campaign: any) => ({
        id: campaign.id,
        name: campaign.name,
        code: campaign.dealId,
        duration: `${campaign.startDate?.dateStr} - ${campaign.endDate?.dateStr}`,
        inventories: campaign.campaignInventories?.length || 0,
        status: campaign.campaignStatus,
        impressions: campaign.summaryReport?.potentialViews || 0,
        totalPrice: campaign.priceSummary?.netTotal || 0,
        bookings: campaign.campaignInventories || [],
        reasonForRejection: campaign.reasonForRejection,
        reporting: campaign.reporting || { proofOfPlay: false, deliveryReports: false }
      }));

      setFilteredCampaigns(transformedCampaigns);
      setTotalElements(totalElements);
      setError(null);

    } else {
      const token = localStorage.getItem('authToken');
      const userId = localStorage.getItem('userId');
      const companyId = localStorage.getItem('companyId');

      if (!token || !userId || !companyId) {
        setError('Missing authentication details');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const { data } = await client.query({
          query: MY_CAMPAIGN_LIST_QUERY,
          variables: {
            accessToken: token,
            userId: userId,
            companyId: companyId,
            sort: 'lastModifiedDate,desc',
            name: appliedSearchTerm || '',
            campaignStatus: statusFilter === 'all' ? '' : statusFilter,
            page: currentPage - 1,
            size: campaignsPerPage
          },
          fetchPolicy: 'network-only'
        });

        const campaigns = data.mycampaignList.content || [];
        const totalElements = data.mycampaignList.totalElements || campaigns.length;

        // Transform API data to match your UI structure
        const transformedCampaigns = campaigns.map((campaign: any) => ({
          id: campaign.id,
          name: campaign.name,
          code: campaign.dealId,
          duration: `${campaign.startDate?.dateStr} - ${campaign.endDate?.dateStr}`,
          inventories: campaign.campaignInventories?.length || 0,
          status: campaign.campaignStatus,
          impressions: campaign.summaryReport?.potentialViews || 0,
          totalPrice: campaign.priceSummary?.netTotal || 0,
          bookings: campaign.campaignInventories || [],
          reasonForRejection: campaign.reasonForRejection,
          reporting: campaign.reporting || { proofOfPlay: false, deliveryReports: false },
          currencyCode: campaign?.priceSummary?.currency?.code
        }));

        setFilteredCampaigns(transformedCampaigns);
        setTotalElements(totalElements);
        setError(null);
      } catch (err) {
        console.error('Error fetching campaign details:', err);
        setError('Error fetching campaign details');
        toast({
          variant: "destructive",
          title: "Error",
          description: "Failed to fetch campaign data"
        });
      } finally {
        setLoading(false);
      }
    }
  };

  // Calculate pagination
  const totalPages = Math.ceil(totalElements / campaignsPerPage);
  const startIndex = (currentPage - 1) * campaignsPerPage;
  const endIndex = Math.min(startIndex + campaignsPerPage, totalElements);
  // For server-side pagination, we don't slice the array since it already contains only the current page data
  const currentCampaigns = filteredCampaigns;

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const formatCurrency = (amount: number, currencyCode?: string) => {
    const formattedAmount = new Intl.NumberFormat('en-AU', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);

    return currencyCode ? `${currencyCode} ${formattedAmount}` : formattedAmount;
  };

  const formatNumber = (num: number) => {
    return new Intl.NumberFormat().format(num);
  };

  // Helper function to get daypart icon
  const getDaypartIcon = (daypart: string) => {
    switch (daypart) {
      case 'Morning':
        return '🌅';
      case 'Afternoon':
        return '☀️';
      case 'Evening':
        return '🌆';
      case 'Night':
        return '🌙';
      default:
        return '⏰';
    }
  };

  // Helper function to format daypart schedules
  const formatDaypartSchedules = (schedules?: string[]) => {
    if (!schedules || schedules.length === 0) return null;

    return schedules.map(daypart => (
      <span key={daypart} className="inline-flex items-center gap-1 px-2 py-0.5 bg-blue-50 text-blue-700 rounded-full text-xs font-medium mr-1 mb-1">
        <span>{getDaypartIcon(daypart)}</span>
        <span>{daypart}</span>
      </span>
    ));
  };

  // Functions for handling cart conflicts
  const loadDraftToCart = (draftCampaign: any) => {
    if (draftCampaign.bookings && draftCampaign.bookings.length > 0) {
      draftCampaign.bookings.forEach((booking: any) => {
        const cartItem = {
          id: booking.id,
          name: booking.name,
          location: `${booking.siteLocation}, ${booking.siteAddress}`,
          state: booking.siteAddress || "",
          price: Math.round(draftCampaign.totalPrice / draftCampaign.bookings!.length),
          impressions: booking.impressions,
          uniqueVisitors: booking.monthlySummary?.uniqueVisitors || 0,
          frequency: booking.monthlySummary?.frequency || 0,
          size: booking.resolution || "N/A",
          type: booking.type || "Digital",
          status: "available" as const,
          image: booking.mainImage,
          specifications: {
            resolution: booking.resolution || "N/A",
            duration: "30 days"
          },
          isSpotBased: booking.isSpotBased || false,
          priceFormat: booking.isSpotBased ? "spot-based" as const : "day-based" as const
        };
        addToCart(cartItem);
      });

      localStorage.setItem('selectedCampaignId', draftCampaign.id);

      toast({
        title: "Draft Campaign Loaded",
        description: `${draftCampaign.name} has been loaded into your cart. You can now add more inventory or continue editing.`,
      });

    }
    // Navigate to cart with draft campaign data
    navigateRouter(`${base_path}/mycart?draftId=${draftCampaign.id}&draftName=${encodeURIComponent(draftCampaign.name)}&startDate=${draftCampaign.startDate || ''}&endDate=${draftCampaign.endDate || ''}&edit=true&source=campaigns`);
    // navigateRouter(`${base_path}/cart?draftId=${draftCampaign.id}&draftName=${encodeURIComponent(draftCampaign.name)}&startDate=${draftCampaign.startDate || ''}&endDate=${draftCampaign.endDate || ''}&notes=${encodeURIComponent(draftCampaign.campaignNotes || '')}`);

  };

  const handleKeepCart = () => {
    toast({
      title: "Cannot Continue",
      description: "You cannot continue editing a draft campaign while inventories are present in the cart.",
      variant: "destructive"
    });
    setShowCartConflictModal(false);
    setPendingDraftCampaign(null);
  };

  const handleSaveCartAsDraft = () => {
    setShowCartConflictModal(false);
    setShowSaveDraftModal(true);
    // Keep pendingDraftCampaign so we can continue with it after saving current cart
  };

  const handleSaveDraftCampaign = async () => {
    if (!draftCampaignName.trim() || !draftStartDate || !draftEndDate) {
      toast({
        title: "Missing Information",
        description: "Please provide campaign name, start date, and end date.",
        variant: "destructive"
      });
      return;
    }

    setIsSavingDraft(true);

    try {
      const draftData = {
        name: draftCampaignName.trim(),
        duration: `${draftStartDate} - ${draftEndDate}`,
        startDate: draftStartDate,
        endDate: draftEndDate,
        campaignNotes: "",
        selectedBillboards: cartItems.map(item => ({
          id: item.id,
          name: item.name,
          price: item.price,
          location: item.location,
          impressions: item.impressions || 0
        })),
        totalBudget: Math.round(cartItems.reduce((sum, item) => sum + (item.price / 100), 0) * 100),
        status: "draft"
      };

      toast({
        title: "Draft Saved!",
        description: `Your current cart has been saved as "${draftData.name}". Now continuing with your original campaign.`,
      });

      // Reset form
      setDraftCampaignName("");
      setDraftStartDate("");
      setDraftEndDate("");
      setShowSaveDraftModal(false);

      // Now continue with the original pending draft campaign (Campaign A)
      if (pendingDraftCampaign) {
        // Clear cart first, then load the original campaign
        clearCart();
        loadDraftToCart(pendingDraftCampaign);
        setPendingDraftCampaign(null);
      } else {
        // Fallback - just clear cart and go to campaigns
        clearCart();
        navigateRouter(`${base_path}/my-campaigns`);
      }

    } catch (error: any) {
      console.error("Draft save error:", error);
      toast({
        title: "Error",
        description: "Failed to save draft. Please try again.",
        variant: "destructive",
      });
    } finally {
      setIsSavingDraft(false);
    }
  };

  // Handle campaign rejection/cancellation (unified workflow)
  const handleRejectCampaign = async () => {
    if (!selectedCampaignToReject || !rejectionReason) {
      toast({
        title: "Missing Information",
        description: "Please select a reason for rejection.",
        variant: "destructive"
      });
      return;
    }

    setIsProcessingRejection(true);

    // try {
    //   // Simple fetch API call instead of apiRequest
    //   const response = await fetch(`/api/my-campaigns"/${selectedCampaignToReject.id}/reject`, {
    //     method: 'POST',
    //     headers: {
    //       'Content-Type': 'application/json'
    //     },
    //     body: JSON.stringify({
    //       reason: rejectionReason,
    //       reasonDetails: rejectionReasonDetails,
    //       rejectedBy: rejectionType === "buyer_cancel" ? "buyer" : "media_owner"
    //     })
    //   });

    //   if (response.ok) {
    //     const actionText = rejectionType === "buyer_cancel" ? "cancelled" : "rejected";
    //     const refundText = rejectionType === "buyer_cancel" && canRejectCampaign(selectedCampaignToReject)
    //       ? " Refund processing has been initiated."
    //       : "";

    //     // Reset modal state
    //     setShowRejectModal(false);
    //     setSelectedCampaignToReject(null);
    //     setRejectionReason("");
    //     setRejectionReasonDetails("");

    //     toast({
    //       title: `Campaign ${actionText.charAt(0).toUpperCase() + actionText.slice(1)}`,
    //       description: `${selectedCampaignToReject.name} has been successfully ${actionText}.${refundText}`,
    //     });

    //     // Refresh campaigns data to reflect the change
    //     window.location.reload();
    //   } else {
    //     throw new Error('Failed to process request');
    //   }

    // } catch (error: any) {
    //   toast({
    //     title: "Action Failed",
    //     description: error.message || "Unable to process request. Please try again or contact support.",
    //     variant: "destructive"
    //   });
    // } finally {
    //   setIsProcessingRejection(false);
    // }
  };

  // Handle action clicks
  const handleActionClick = (action: string, campaignId: string) => {

    switch (action) {
      case "proof":
        navigateRouter(`${base_path}/campaigns/${campaignId}/proof-of-play`);
        break;
      case "delivery":
        navigateRouter(`${base_path}/delivery-report?campaign=${campaignId}`);
        break;
      case "invoice":
        navigateRouter(`${base_path}/payment-invoice?campaign=${campaignId}`);
        break;
      case "creative":
        navigateRouter(`${base_path}/creative-management?campaign=${campaignId}`);
        break;
      case "continue":
        // Handle continue action for draft campaigns
        const draftCampaign = filteredCampaigns.find(c => c.id.toString() === campaignId);
        if (draftCampaign && (draftCampaign.status === "Draft" || draftCampaign.status === "GENERATED")) {
          // Check if cart has items
          if (cartCount > 0) {
            // Show cart conflict modal
            setPendingDraftCampaign(draftCampaign);
            setShowCartConflictModal(true);
          } else {
            // Cart is empty, proceed directly
            loadDraftToCart(draftCampaign);
          }
        }
        break;
      case "pay":
        // Handle payment action for pending payment campaigns - go to checkout
        navigateRouter(`${base_path}/checkout?campaign=${campaignId}`);
        break;
      case "reject":
        // Handle campaign rejection/cancellation (unified workflow)
        const campaignToReject = filteredCampaigns.find(c => c.id.toString() === campaignId);
        if (campaignToReject) {
          setSelectedCampaignToReject(campaignToReject);
          setRejectionType("buyer_cancel"); // Default to buyer cancellation
          setShowRejectModal(true);
        }
        break;
      case "review":
        // Handle campaign review action for completed campaigns
        navigateRouter(`/campaign-review?campaign=${campaignId}`);
        break;
      case "view_reason":
        // Show rejection reason modal
        const rejectedCampaign = filteredCampaigns.find(c => c.id.toString() === campaignId);
        if (rejectedCampaign && rejectedCampaign.reasonForRejection) {
          setSelectedRejectionReason(rejectedCampaign.reasonForRejection);
          setShowRejectionReasonModal(true);
        } else {
          setSelectedRejectionReason("No rejection reason provided");
          setShowRejectionReasonModal(true);
        }
        break;
      default:
        break;
    }
  };

  // Create unified inventory for consistent indexing across all tabs (map, gallery)
  // This version is stable and doesn't change based on search to prevent map jumping
  const unifiedInventory = useMemo(() => {
    if (!showBookingModal) return [];

    const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];

    // Create all regular inventory items without search filtering
    const allRegularInventory = regularInventories.map((item: any) => ({
      // Normalize the data structure for consistent access
      id: item.inventoryId || item.id,
      name: item.inventoryName || item.name,
      code: item.inventoryReferenceId || item.code,
      siteLocation: item.inventoryAddress || item.siteLocation,
      category: item.inventoryVenueType || item.category,
      type: item.inventoryType || item.type,
      resolution: item.inventoryResolution || item.specifications?.resolution,
      originalIndex: regularInventories.indexOf(item),
      originalData: item,
      isFromPackage: false
    }));

    // Group regular inventory by type
    const digitalInventory = allRegularInventory.filter((item: any) =>
      item.type && item.type.toLowerCase().includes('digital')
    );
    const classicInventory = allRegularInventory.filter((item: any) =>
      item.type && (item.type.toLowerCase().includes('classic') || item.type.toLowerCase().includes('static'))
    );
    const packageInventory = allRegularInventory.filter((item: any) =>
      item.type && item.type.toLowerCase().includes('package')
    );
    const dealInventory = allRegularInventory.filter((item: any) =>
      item.type && item.type.toLowerCase().includes('deal')
    );
    const otherInventory = allRegularInventory.filter((item: any) => {
      if (!item.type) return true;
      const typeLower = item.type.toLowerCase();
      return !typeLower.includes('digital') &&
        !typeLower.includes('classic') &&
        !typeLower.includes('package') &&
        !typeLower.includes('static') &&
        !typeLower.includes('deal');
    });

    // Handle package sub-inventories if package details are loaded
    let packageDigitalInventory = [];
    let packageClassicInventory = [];
    if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
      const packageSubInventories = packageDetails.campaignInventories.map((item: any) => ({
        id: item.inventoryId || item.id,
        name: item.inventoryName || item.name,
        code: item.inventoryReferenceId || item.code,
        siteLocation: item.inventoryAddress || item.siteLocation,
        category: item.inventoryVenueType || item.category,
        type: item.inventoryType || item.type,
        resolution: item.inventoryResolution || item.specifications?.resolution,
        originalIndex: regularInventories.length + packageDetails.campaignInventories.indexOf(item), // Offset by regular inventory length
        originalData: item,
        isFromPackage: true
      }));

      packageDigitalInventory = packageSubInventories.filter((item: any) =>
        item.type && item.type.toLowerCase().includes('digital')
      );
      packageClassicInventory = packageSubInventories.filter((item: any) =>
        item.type && (item.type.toLowerCase().includes('classic') || item.type.toLowerCase().includes('static'))
      );
    }

    // Create unified inventory for consistent indexing across all tabs (map, gallery)
    return [
      ...digitalInventory,
      ...classicInventory,
      ...packageInventory,
      ...dealInventory,
      ...otherInventory,
      ...packageDigitalInventory,
      ...packageClassicInventory
    ];
  }, [selectedCampaignBookings, packageDetails, showBookingModal]);

  // Create filtered inventory for search functionality (used only in sidebar)
  const filteredUnifiedInventory = useMemo(() => {
    if (!modalSearchTerm.trim()) return unifiedInventory;

    const searchLower = modalSearchTerm.toLowerCase();
    return unifiedInventory.filter((item: any) => {
      return (
        (item.name)?.toLowerCase().includes(searchLower) ||
        (item.code)?.toLowerCase().includes(searchLower) ||
        (item.siteLocation)?.toLowerCase().includes(searchLower)
      );
    });
  }, [unifiedInventory, modalSearchTerm]);

  // Package Details API call function
  const fetchPackageDetails = async (packageId: string) => {
    setIsLoadingPackage(true);
    setPackageError(null);

    try {
      const authToken = localStorage.getItem('authToken');

      const { data } = await client.query({
        query: GET_PACKAGE_DETAILS,
        variables: {
          packageId: packageId,
          accessToken: authToken,
          page: 1,
          size: 10
        },
        fetchPolicy: "network-only"
      });

      if (data?.packageDetails) {
        // Transform package inventories to match normal billboard structure
        const normalizedInventories = data.packageDetails.inventories?.map((inventory: any, index: number) => ({
          // Map package structure to normal campaignInventories structure
          id: inventory.inventoryId || inventory.id || `package-${index}`,
          inventoryId: inventory.inventoryId || inventory.id || `package-${index}`,
          inventoryName: inventory.name || `Billboard ${index + 1}`,
          inventoryThumbnailUrl: inventory.thumbnail || THUMBNAIL_IMAGES[index % THUMBNAIL_IMAGES.length],
          inventoryAddress: inventory.location || 'N/A',
          inventoryReferenceId: inventory.referenceId || `REF${index + 1}`,
          inventoryResolution: inventory.size || 'N/A',
          inventoryVenueType: inventory.venueType || 'Standard',
          inventoryLatitude: inventory.latitude,
          inventoryLongitude: inventory.longitude,
          inventoryReports: {
            totalPotentialViews: inventory.estimation?.potentialViews || Math.floor(Math.random() * 100000) + 10000
          },
          reasonForRejection: '',
          nowPayPrice: inventory.price || inventory.sellingRate || Math.floor(Math.random() * 5000) + 1000,
          mediaOwnerName: data.packageDetails.publisher?.name || 'Media Owner',
          inventoryType: inventory.type || 'Digital',
          // Add package-specific flag to identify these as package inventories
          isPackageInventory: true,
          packageId: packageId,
          packageName: data.packageDetails.name
        })) || [];

        // Create a normalized package details object that matches campaign structure
        const normalizedPackageDetails = {
          ...data.packageDetails,
          campaignInventories: normalizedInventories,
          // Add flag to identify this as package data
          isPackageData: true
        };
        console.log("Normalized package details:", normalizedPackageDetails);

        setPackageDetails(normalizedPackageDetails);

        // Convert normalized inventories to billboard format for map integration
        if (normalizedInventories.length > 0) {
          const convertedBillboards = convertInventoriesToBillboards(normalizedInventories);
          setBillboardsForMap(convertedBillboards);
          setSelectedInventoryForMap(convertedBillboards[0] || null);
        }
      } else {
        setPackageError('No package details found');
      }
    } catch (error: any) {
      console.error('Error fetching package details:', error);
      setPackageError(error.message || 'Failed to fetch package details');
    } finally {
      setIsLoadingPackage(false);
    }
  };

  // Fetch individual package details for sidebar expansion
  const fetchIndividualPackageDetails = async (packageId: string) => {
    const newLoadingPackages = new Set(loadingPackages);
    newLoadingPackages.add(packageId);
    setLoadingPackages(newLoadingPackages);

    try {
      const authToken = localStorage.getItem('authToken');

      const { data } = await client.query({
        query: GET_PACKAGE_DETAILS,
        variables: {
          packageId: packageId,
          accessToken: authToken,
          page: 1,
          size: 10
        },
        fetchPolicy: "network-only"
      });

      if (data?.packageDetails) {
        console.log("Fetched individual package details:", data.packageDetails);

        // Transform package inventories to match normal billboard structure
        const normalizedInventories = data.packageDetails.inventories?.map((inventory: any, index: number) => ({
          // Map package structure to normal campaignInventories structure
          id: inventory.inventoryId || inventory.id || `package-${packageId}-${index}`,
          inventoryId: inventory.inventoryId || inventory.id || `package-${packageId}-${index}`,
          inventoryName: inventory.name || `Billboard ${index + 1}`,
          inventoryThumbnailUrl: inventory.thumbnail || THUMBNAIL_IMAGES[index % THUMBNAIL_IMAGES.length],
          inventoryAddress: inventory.location || 'N/A',
          inventoryReferenceId: inventory.referenceId || `REF${index + 1}`,
          inventoryResolution: inventory.size || 'N/A',
          inventoryVenueType: inventory.venueType || 'Standard',
          inventoryLatitude: inventory.latitude,
          inventoryLongitude: inventory.longitude,
          inventoryReports: {
            totalPotentialViews: inventory.estimation?.potentialViews || Math.floor(Math.random() * 100000) + 10000
          },
          reasonForRejection: '',
          nowPayPrice: inventory.price || inventory.sellingRate || Math.floor(Math.random() * 5000) + 1000,
          mediaOwnerName: data.packageDetails.publisher?.name || 'Media Owner',
          inventoryType: inventory.type || 'Digital',
          // Add package-specific flag to identify these as package inventories
          isPackageInventory: true,
          packageId: packageId,
          packageName: data.packageDetails.name
        })) || [];

        // Store individual package details
        const newIndividualPackageDetails = new Map(individualPackageDetails);
        newIndividualPackageDetails.set(packageId, {
          ...data.packageDetails,
          campaignInventories: normalizedInventories,
          isPackageData: true
        });
        setIndividualPackageDetails(newIndividualPackageDetails);
      }
    } catch (error: any) {
      console.error('Error fetching individual package details:', error);
    } finally {
      const newLoadingPackages = new Set(loadingPackages);
      newLoadingPackages.delete(packageId);
      setLoadingPackages(newLoadingPackages);
    }
  };

  // Map initialization and management functions (adapted from promotional)
  const cleanupMap = () => {
    if (map) {
      try {
        if (currentPopup) {
          currentPopup.remove();
          setCurrentPopup(null);
        }
        if (marker) {
          marker.remove();
          setMarker(null);
        }
        map.remove();
      } catch (error) {
        console.error('Error cleaning up map:', error);
      } finally {
        setMap(null);
      }
    }
  };

  // Map initialization effect
  useEffect(() => {
    if (activeTab === 'map' && showBookingModal) {
      const token = MAPBOX_TOKEN || '';
      if (!token) {
        console.error('Mapbox token missing');
        return;
      }

      mapboxgl.accessToken = token;

      const timer = setTimeout(() => {
        // Check if container is ready before initializing
        if (!mapContainer.current) {
          console.log('Map container not ready, skipping initialization');
          return;
        }

        // Clean up existing map if it exists but the container is different
        if (map && (!map.getContainer() || map.getContainer() !== mapContainer.current)) {
          console.log('Cleaning up existing map for re-initialization');
          cleanupMap();
        }

        // Only initialize if no map exists or if we cleaned it up
        if (!map) {
          try {
            // Get current selected inventory from unified list
            const currentItem = unifiedInventory[selectedInventoryIndex];

            // Use coordinates if available, otherwise default - be more flexible about coordinate location
            const latitude = currentItem?.originalData?.inventoryLatitude ||
              currentItem?.originalData?.latitude ||
              currentItem?.latitude;
            const longitude = currentItem?.originalData?.inventoryLongitude ||
              currentItem?.originalData?.longitude ||
              currentItem?.longitude;

            const lat = (latitude && !isNaN(Number(latitude))) ? Number(latitude) : 1.3521; // Singapore default
            const lng = (longitude && !isNaN(Number(longitude))) ? Number(longitude) : 103.8198;

            const initialMap = new mapboxgl.Map({
              container: mapContainer.current!,
              style: 'mapbox://styles/mapbox/streets-v11',
              center: [lng, lat],
              zoom: 12,
              interactive: true,
              attributionControl: false
            });

            initialMap.addControl(new mapboxgl.NavigationControl(), 'top-right');
            setMap(initialMap);

            initialMap.on('load', () => {
              initialMap.resize();

              if (latitude && longitude && !isNaN(Number(latitude)) && !isNaN(Number(longitude))) {
                const newMarker = new mapboxgl.Marker({ color: '#10b981' })
                  .setLngLat([Number(longitude), Number(latitude)])
                  .addTo(initialMap);

                setMarker(newMarker);
                newMarker.getElement().onclick = () => showPopup(currentItem.originalData);
              }
            });

            initialMap.on('error', (e) => {
              console.error('Map initialization error:', e);
            });
          } catch (error) {
            console.error('Error initializing Mapbox map:', error);
          }
        } else {
          // Map exists, just resize it
          try {
            console.log('Resizing existing map');
            map.resize();
          } catch (error) {
            console.error('Error resizing existing map:', error);
          }
        }
      }, 300);

      return () => {
        clearTimeout(timer);
      };
    }
  }, [activeTab, showBookingModal, selectedInventoryIndex, selectedCampaignBookings, unifiedInventory, mapForceRender]);

  const formatDuration = (duration: any): string => {
    if (!duration || typeof duration !== 'string') return 'N/A';

    try {
      const [start, end] = duration.split(' - ').map(d => new Date(d.trim()));
      if (isNaN(start.getTime()) || isNaN(end.getTime())) return 'N/A';

      const formatDate = (date: Date): string => {
        const day = date.getDate().toString().padStart(2, '0');
        const month = date.toLocaleDateString('en-GB', { month: 'short' }).replace('Sept', 'Sep');
        const year = date.getFullYear();
        return `${day} ${month} ${year}`;
      };

      return `${formatDate(start)} - ${formatDate(end)}`;
    } catch (error) {
      console.error('Error formatting duration:', error, duration);
      return 'N/A';
    }
  };
  // Update marker when selected inventory changes
  useEffect(() => {
    if (map && unifiedInventory.length > 0) {
      const currentItem = unifiedInventory[selectedInventoryIndex];

      // Use flexible coordinate checking
      const latitude = currentItem?.originalData?.inventoryLatitude ||
        currentItem?.originalData?.latitude ||
        currentItem?.latitude;
      const longitude = currentItem?.originalData?.inventoryLongitude ||
        currentItem?.originalData?.longitude ||
        currentItem?.longitude;

      if (latitude && longitude && !isNaN(Number(latitude)) && !isNaN(Number(longitude))) {
        const newLat = Number(latitude);
        const newLng = Number(longitude);

        map.flyTo({
          center: [newLng, newLat],
          zoom: 14,
          essential: true,
          speed: 0.5
        });

        if (marker) {
          marker.setLngLat([newLng, newLat]);
          marker.getElement().onclick = () => showPopup(currentItem.originalData);
        } else {
          const newMarker = new mapboxgl.Marker({ color: '#10b981' })
            .setLngLat([newLng, newLat])
            .addTo(map);
          setMarker(newMarker);
          newMarker.getElement().onclick = () => showPopup(currentItem.originalData);
        }
      }
    }
  }, [map, selectedInventoryIndex, unifiedInventory]);

  // Resize map when tab becomes active
  useEffect(() => {
    if (map && activeTab === 'map' && showBookingModal) {
      // Use multiple timeouts to ensure proper rendering
      const resizeTimer1 = setTimeout(() => {
        try {
          map.resize();
        } catch (error) {
          console.error('Error resizing map (first attempt):', error);
        }
      }, 100);

      const resizeTimer2 = setTimeout(() => {
        try {
          map.resize();

          const currentItem = unifiedInventory[selectedInventoryIndex];

          // Use flexible coordinate checking
          const latitude = currentItem?.originalData?.inventoryLatitude ||
            currentItem?.originalData?.latitude ||
            currentItem?.latitude;
          const longitude = currentItem?.originalData?.inventoryLongitude ||
            currentItem?.originalData?.longitude ||
            currentItem?.longitude;

          if (latitude && longitude && !isNaN(Number(latitude)) && !isNaN(Number(longitude))) {
            map.flyTo({
              center: [Number(longitude), Number(latitude)],
              zoom: 14,
              essential: true
            });
          }
        } catch (error) {
          console.error('Error resizing map (second attempt):', error);
        }
      }, 500);

      return () => {
        clearTimeout(resizeTimer1);
        clearTimeout(resizeTimer2);
      };
    }
  }, [map, activeTab, showBookingModal, selectedInventoryIndex, unifiedInventory]);

  // Cleanup map when modal closes or switching away from map tab
  useEffect(() => {
    if (!showBookingModal || activeTab !== 'map') {
      if (map && activeTab !== 'map') {
        // Only cleanup when switching away from map tab, not when modal closes
        console.log('Cleaning up map on tab switch');
        cleanupMap();
      } else if (!showBookingModal) {
        // Cleanup when modal closes
        cleanupMap();
      }
    }
    return () => {
      if (!showBookingModal) {
        cleanupMap();
      }
    };
  }, [showBookingModal, activeTab]);

  // Popup handler for map markers
  const showPopup = (inventory: any) => {
    if (marker && map) {
      if (currentPopup) {
        currentPopup.remove();
        setCurrentPopup(null);
      }
      if (marker.getPopup()) {
        marker.setPopup(null);
      }

      const popupContent = `
        <div style="min-width: 250px; font-family: system-ui, -apple-system, sans-serif;">
          <div style="margin-bottom: 12px;">
            <img src="${inventory.inventoryThumbnailUrl || 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=250&h=150&fit=crop&auto=format'}" 
                 alt="Billboard Image" 
                 style="width: 100%; height: 120px; object-fit: cover; border-radius: 8px;">
          </div>
          <div>
            <h3 style="margin: 0 0 8px 0; font-size: 16px; font-weight: 600; color: #111827;">
              ${inventory.inventoryName || inventory.name || 'N/A'}
            </h3>
            <p style="margin: 0 0 4px 0; font-size: 14px; color: #6b7280;">
              ${inventory.inventoryAddress || inventory.siteLocation || 'N/A'}
            </p>
            <p style="margin: 0 0 4px 0; font-size: 14px; color: #6b7280;">
              Type: ${inventory.inventoryType || inventory.type || 'N/A'}
            </p>
            <p style="margin: 0; font-size: 14px; color: #6b7280;">
              Resolution: ${inventory.inventoryResolution || inventory.specifications?.resolution || 'N/A'}
            </p>
          </div>
        </div>
      `;

      const popup = new mapboxgl.Popup({
        offset: [0, -10],
        anchor: 'bottom',
        closeButton: true,
        closeOnClick: true
      })
        .setHTML(popupContent)
        .setLngLat([inventory.inventoryLongitude || 0, inventory.inventoryLatitude || 0]);

      marker.setPopup(popup);
      popup.addTo(map);
      setCurrentPopup(popup);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">

      <div className="max-w-[95vw] mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="mb-8">
          <div className="flex justify-between items-center">
            <div>
              <h1 className="text-3xl font-bold text-emerald-600">My Campaigns</h1>
              <p className="text-gray-600 mt-2">Manage and track your advertising campaigns</p>
            </div>
            <div className="flex space-x-2">
              <Link to={`${base_path}/propertylist`}>
                <Button className="btn-emerald-gradient">
                  <Plus className="h-4 w-4 mr-2" />
                  Start New Campaign
                </Button>
              </Link>
            </div>
          </div>
        </div>

        {/* Search and Filters */}
        {isFieldEnabled('dropdownstatuslist') || isFieldEnabled('searchInOrders') || isFieldEnabled('customiseTable') ? (
          <Card className="mb-6">
            <CardContent className="p-6">
              <div className="flex flex-col md:flex-row gap-4">
                {isFieldEnabled('searchInOrders') && (
                  <div className="flex-1 relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-emerald-600 z-10" />
                    <Input
                      placeholder="Search campaigns"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      onKeyDown={handleSearchKeyDown}
                      className="px-5 pl-10 pr-10 bg-white border border-gray-300 rounded-md shadow-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 h-10"
                      style={{ backgroundColor: 'white' }}
                    />
                    {searchQuery && (
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleResetSearch}
                        className="absolute right-2 top-1/2 transform -translate-y-1/2 h-6 w-6 p-0 hover:bg-gray-200 rounded-full"
                        title="Clear search"
                      >
                        <X className="h-3 w-3 text-gray-400" />
                      </Button>
                    )}
                    {searchError && (
                      <div className="absolute top-full mt-1 text-red-600 text-sm">
                        {searchError}
                      </div>
                    )}
                  </div>
                )}
                <div className="flex gap-2">
                  {isFieldEnabled('dropdownstatuslist') && (
                    <Select value={statusFilter} onValueChange={(value) => {
                      setStatusFilter(value);
                      setCurrentPage(1);
                    }}>
                      <SelectTrigger className="w-48 bg-white border border-gray-300 rounded-md shadow-sm focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 h-10" style={{ backgroundColor: 'white' }}>
                        <Filter className="h-4 w-4 mr-2 text-emerald-600" />
                        <SelectValue placeholder="Filter by status" />
                      </SelectTrigger>
                      <SelectContent className="bg-white border border-gray-200 shadow-lg rounded-md z-50" style={{ backgroundColor: 'white' }}>
                        <SelectItem value="all" className="hover:bg-gray-50 cursor-pointer mx-2 text-sm">All Campaigns</SelectItem>
                        <SelectItem value="GENERATED" className="hover:bg-gray-50 cursor-pointer mx-2 text-sm">Draft</SelectItem>
                        <SelectItem value="REQUESTED" className="hover:bg-gray-50 cursor-pointer mx-2 text-sm">Requested</SelectItem>
                        <SelectItem value="RE_OPENED" className="hover:bg-gray-50 cursor-pointer mx-2 text-sm">Re-Opened</SelectItem>
                        <SelectItem value="PAYMENT_PENDING" className="hover:bg-gray-50 cursor-pointer mx-2 text-sm">Payment Pending</SelectItem>
                        <SelectItem value="APPROVED" className="hover:bg-gray-50 cursor-pointer mx-2 text-sm">Approved</SelectItem>
                        <SelectItem value="REJECTED" className="hover:bg-gray-50 cursor-pointer mx-2 text-sm">Rejected</SelectItem>
                        <SelectItem value="LIVE" className="hover:bg-gray-50 cursor-pointer mx-2 text-sm">Live</SelectItem>
                        <SelectItem value="COMPLETED" className="hover:bg-gray-50 cursor-pointer mx-2 text-sm">Completed</SelectItem>
                      </SelectContent>
                    </Select>
                  )}
                  {isFieldEnabled('customiseTable') && (
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="outline" className="bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:border-emerald-500 focus:ring-1 focus:ring-emerald-500 h-10" style={{ backgroundColor: 'white' }}>
                          <Settings className="h-4 w-4 mr-2 text-emerald-600" />
                          Customize table
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end" className="w-56 bg-white border border-gray-200 shadow-lg rounded-md z-50" style={{ backgroundColor: 'white' }}>
                        <div className="p-2">
                          <div className="text-sm font-medium mb-2 text-emerald-600">Show/Hide Columns</div>
                          {Object.entries(visibleColumns).map(([key, value]) => (
                            <div
                              key={key}
                              className="flex items-center space-x-2 py-1 px-2 rounded hover:bg-gray-50 cursor-pointer"
                              onClick={() => setVisibleColumns(prev => ({
                                ...prev,
                                [key as keyof typeof visibleColumns]: !prev[key as keyof typeof visibleColumns]
                              }))}
                            >
                              <input
                                type="checkbox"
                                checked={value}
                                onChange={() => { }} // Empty handler since div click handles it
                                className="w-4 h-4 checkbox-amber rounded pointer-events-none"
                              />
                              <span className="text-sm capitalize">
                                {key === 'totalPrice' ? 'Total Price' : key}
                              </span>
                            </div>
                          ))}
                        </div>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ) : null}
        {/* Campaigns Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Campaigns ({totalElements})</span>
              <Badge variant="outline" className="ml-2">
                {totalElements} results
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            {loading && (
              <div className="text-center py-12">
                <div className="text-emerald-600 text-lg">Loading campaigns...</div>
                <p className="text-gray-400 mt-2">Please wait while we fetch your data</p>
              </div>
            )}
            {error && (
              <div className="text-center py-12">
                <div className="text-red-600 text-lg">Error loading campaigns</div>
                <p className="text-gray-400 mt-2">{error}</p>
                <Button
                  onClick={fetchCampaignDetails}
                  className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                >
                  Retry
                </Button>
              </div>
            )}
            {!loading && !error && (
              <div className="overflow-x-auto">
                <Table className="table-fixed">
                  <TableHeader>
                    <TableRow>
                      {visibleColumns.campaign && <TableHead className="w-[250px] px-2">Campaign</TableHead>}
                      {visibleColumns.duration && <TableHead className="w-[120px] px-2">Duration</TableHead>}
                      {visibleColumns.inventories && <TableHead className="w-[100px] px-2 text-center">Inventories</TableHead>}
                      {visibleColumns.status && <TableHead className="w-[140px] px-2">Campaign Status</TableHead>}
                      {visibleColumns.impressions && <TableHead className="w-[120px] px-2">Impressions</TableHead>}
                      {visibleColumns.totalPrice && <TableHead className="w-[150px] px-2 text-left">Total Price</TableHead>}
                      {visibleColumns.bookings && <TableHead className="w-[100px] px-2 text-center">Bookings</TableHead>}
                      {visibleColumns.action && <TableHead className="w-[80px] px-2 text-center">Action</TableHead>}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {currentCampaigns.map((campaign) => (
                      <TableRow key={campaign.id} className="hover:bg-gray-50">
                        {visibleColumns.campaign && (
                          <TableCell className="px-2">
                            <div className="space-y-1">
                              <div className="font-medium text-gray-900 truncate">{campaign.name}</div>
                              <div className="text-sm text-gray-500 truncate">{campaign.code}</div>
                            </div>
                          </TableCell>
                        )}
                        {visibleColumns.duration && (
                          <TableCell className="px-2">
                            <div className="text-sm">
                              {formatDuration(campaign.duration)}
                            </div>
                          </TableCell>
                        )}
                        {visibleColumns.inventories && (
                          <TableCell className="px-2">
                            <div className="text-center">
                              {campaign.inventories}
                            </div>
                          </TableCell>
                        )}
                        {visibleColumns.status && (
                          <TableCell className="px-2">
                            <Badge
                              variant="secondary"
                              className={statusColors[campaign.status as keyof typeof statusColors]}
                            >
                              {campaign.status === "GENERATED" ? "DRAFT" :
                                campaign.status === "RE_OPENED" ? "RE-OPENED" :
                                  campaign.status === "PAYMENT_PENDING" ? "PAYMENT PENDING" :
                                    campaign.status}
                            </Badge>
                          </TableCell>
                        )}
                        {visibleColumns.impressions && (
                          <TableCell className="px-2">
                            <div className="text-sm font-medium">
                              {formatNumber(campaign.impressions)}
                            </div>
                          </TableCell>
                        )}
                        {visibleColumns.totalPrice && (
                          <TableCell className="px-2">
                            <div className="text-sm font-semibold text-left">
                              {(() => {
                                console.log('Formatting total price for campaign:', campaign.currencyCode);

                                const priceSummary = campaign.priceSummary;
                                const currencyCode = campaign.currencyCode || 'N/A';
                                console.log('Campaign ID:', campaign.id, 'priceSummary:', priceSummary, 'Currency Code:', currencyCode, 'Total Price:', campaign.totalPrice);
                                return formatCurrency(campaign.totalPrice, currencyCode);
                              })()}
                            </div>
                          </TableCell>
                        )}
                        {visibleColumns.bookings && (
                          <TableCell className="px-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-emerald-600 hover:text-emerald-800"
                              onClick={() => {
                                setSelectedCampaignBookings(campaign);

                                console.log("Selected campaign bookings:", campaign);

                                // Check if campaign has package inventories
                                const packageInventory = campaign.bookings?.find(
                                  inv => inv.inventoryType.toLowerCase() === "package"
                                );

                                if (packageInventory) {
                                  // Handle package inventory - call GET_PACKAGE_DETAILS API
                                  const packageId = packageInventory.inventoryId || packageInventory.id;
                                  if (packageId) {
                                    fetchPackageDetails(packageId);
                                  } else {
                                    toast({
                                      title: "Error",
                                      description: "Package ID not found.",
                                      variant: "destructive"
                                    });
                                  }
                                } else {
                                  // Handle regular billboard inventories
                                  if (campaign.campaignInventories && campaign.campaignInventories.length > 0) {
                                    const convertedBillboards = convertInventoriesToBillboards(campaign.campaignInventories);
                                    setBillboardsForMap(convertedBillboards);
                                    setSelectedInventoryForMap(convertedBillboards[0] || null);
                                  } else if (campaign.bookings && campaign.bookings.length > 0) {
                                    // Fallback to bookings if campaignInventories is not available
                                    const convertedBillboards = convertInventoriesToBillboards(campaign.bookings);
                                    setBillboardsForMap(convertedBillboards);
                                    setSelectedInventoryForMap(convertedBillboards[0] || null);
                                  } else {
                                    setBillboardsForMap([]);
                                    setSelectedInventoryForMap(null);
                                    toast({
                                      title: "No Inventory",
                                      description: "No inventory data available for this campaign.",
                                      variant: "default"
                                    });
                                  }
                                }

                                setSelectedInventoryIndex(0);
                                setSelectedMainImage(campaign.bookings?.[0]?.mainImage || MAIN_IMAGES[0]);
                                setModalSearchTerm("");
                                setShowBookingModal(true);
                              }}
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Show
                            </Button>
                          </TableCell>
                        )}
                        {visibleColumns.action && (
                          <TableCell className="px-2 text-center">
                            {(() => {
                              const actionItems = getActionItems(campaign.status, campaign);

                              if (actionItems.length === 0) {
                                return (
                                  <div className="flex items-center justify-center text-sm text-gray-500">
                                    <Clock className="h-4 w-4 mr-1" />
                                    Pending
                                  </div>
                                );
                              }

                              return (
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm">
                                      <MoreHorizontal className="h-4 w-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end" className="w-48 bg-white border border-gray-200 shadow-lg rounded-md z-50" style={{ backgroundColor: 'white' }}>
                                    {actionItems.map((item, index) => (
                                      <DropdownMenuItem
                                        key={index}
                                        onClick={() => handleActionClick(item.action, campaign.id)}
                                        className="hover:bg-gray-50 cursor-pointer px-3 py-2 text-sm"
                                      >
                                        <item.icon className={`h-4 w-4 mr-2 ${item.color || 'text-emerald-600'}`} />
                                        {item.label}
                                      </DropdownMenuItem>
                                    ))}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              );
                            })()}
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination Controls */}
            {totalElements > 0 && (
              <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-t">
                <div className="flex items-center text-sm text-gray-700">
                  <span>
                    Showing {startIndex + 1} to {endIndex} of {totalElements} campaigns
                  </span>
                </div>
                <div className="flex items-center space-x-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                    className="flex items-center space-x-1"
                  >
                    <ChevronLeft className="h-4 w-4" />
                    <span>Previous</span>
                  </Button>

                  <div className="flex items-center space-x-1">
                    {(() => {
                      if (totalPages <= 5) {
                        return Array.from({ length: totalPages }, (_, i) => i + 1).map((page) => (
                          <Button
                            key={page}
                            variant={currentPage === page ? "default" : "outline"}
                            size="sm"
                            onClick={() => handlePageChange(page)}
                            className={`w-8 h-8 p-0 ${currentPage === page ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}
                          >
                            {page}
                          </Button>
                        ));
                      }

                      const pages = [];

                      // Always show first page
                      pages.push(
                        <Button key={1} variant={currentPage === 1 ? "default" : "outline"} size="sm" onClick={() => handlePageChange(1)} className={`w-8 h-8 p-0 ${currentPage === 1 ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}>1</Button>
                      );

                      // Show dots if needed
                      if (currentPage > 4) pages.push(<span key="dots1" className="px-2 text-gray-500">...</span>);

                      // Show pages around current
                      const start = Math.max(2, currentPage - 1);
                      const end = Math.min(totalPages - 1, currentPage + 1);
                      for (let i = start; i <= end; i++) {
                        if (i !== 1 && i !== totalPages) {
                          pages.push(
                            <Button key={i} variant={currentPage === i ? "default" : "outline"} size="sm" onClick={() => handlePageChange(i)} className={`w-8 h-8 p-0 ${currentPage === i ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}>{i}</Button>
                          );
                        }
                      }

                      // Show dots if needed
                      if (currentPage < totalPages - 3) pages.push(<span key="dots2" className="px-2 text-gray-500">...</span>);

                      // Always show last page
                      if (totalPages > 1) {
                        pages.push(
                          <Button key={totalPages} variant={currentPage === totalPages ? "default" : "outline"} size="sm" onClick={() => handlePageChange(totalPages)} className={`w-8 h-8 p-0 ${currentPage === totalPages ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-600' : ''}`}>{totalPages}</Button>
                        );
                      }

                      return pages;
                    })()}
                  </div>

                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    className="flex items-center space-x-1"
                  >
                    <span>Next</span>
                    <ChevronRight className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            )}

            {totalElements === 0 && !loading && !error && (
              <div className="text-center py-12">
                <div className="text-gray-500 text-lg">No campaigns found</div>
                <p className="text-gray-400 mt-2">Try adjusting your search or filters</p>
              </div>
            )}

          </CardContent>
        </Card>
      </div>

      {/* Booking Show Modal */}
      <Dialog open={showBookingModal} onOpenChange={handleModalClose}>
        <DialogContent className="max-w-6xl w-[98vw] max-h-[90vh] overflow-hidden p-0 bg-white">
          <DialogHeader className="border-b px-4 py-1 bg-emerald-50 border-emerald-200">
            <div className="flex items-center justify-between">
              <div>
                <DialogTitle className="text-lg font-semibold text-emerald-800">
                  Inventory List
                </DialogTitle>
                <DialogDescription className="text-sm text-emerald-600">
                  Inventory Overview
                </DialogDescription>
              </div>
            </div>
          </DialogHeader>

          <div className="flex lg:flex-row flex-1 overflow-hidden" style={{ height: 'calc(90vh - 60px)' }}>
            {/* Left Sidebar - Search + List */}
            <div className="w-full lg:w-72 border-r border-emerald-200 bg-emerald-50/30">
              {/* Search Bar */}
              <div className="p-1 border-b border-emerald-200">
                <div className="relative">
                  <Search className="h-4 w-4 text-emerald-500 absolute left-3 top-1/2 transform -translate-y-1/2" />
                  <Input
                    placeholder="Search Inventory"
                    className="px-5 w-full pl-10 h-8 text-sm border-emerald-200 focus:border-emerald-400 focus:ring-emerald-200 bg-white"
                    value={modalSearchTerm}
                    onChange={(e) => setModalSearchTerm(e.target.value)}
                  />
                </div>
              </div>

              {/* Inventory List - Auto size with scroll */}
              <div className="p-1 overflow-y-auto" style={{ maxHeight: 'calc(90vh - 120px)' }}>{(() => {
                // Get inventory data from unified inventory
                const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];

                // Group filtered inventory by type for sidebar display
                const digitalInventory = filteredUnifiedInventory.filter((item: any) =>
                  item.type && item.type.toLowerCase().includes('digital') && !item.isFromPackage
                );
                const classicInventory = filteredUnifiedInventory.filter((item: any) =>
                  item.type && (item.type.toLowerCase().includes('classic') || item.type.toLowerCase().includes('static')) && !item.isFromPackage
                );
                const packageInventory = filteredUnifiedInventory.filter((item: any) =>
                  item.type && item.type.toLowerCase().includes('package') && !item.isFromPackage
                );
                const dealInventory = filteredUnifiedInventory.filter((item: any) =>
                  item.type && item.type.toLowerCase().includes('deal') && !item.isFromPackage
                );
                const otherInventory = filteredUnifiedInventory.filter((item: any) => {
                  if (item.isFromPackage) return false;
                  if (!item.type) return true;
                  const typeLower = item.type.toLowerCase();
                  return !typeLower.includes('digital') &&
                    !typeLower.includes('classic') &&
                    !typeLower.includes('package') &&
                    !typeLower.includes('static') &&
                    !typeLower.includes('deal');
                });

                // Get package sub-inventories from filtered inventory
                const packageDigitalInventory = filteredUnifiedInventory.filter((item: any) =>
                  item.type && item.type.toLowerCase().includes('digital') && item.isFromPackage
                );
                const packageClassicInventory = filteredUnifiedInventory.filter((item: any) =>
                  item.type && (item.type.toLowerCase().includes('classic') || item.type.toLowerCase().includes('static')) && item.isFromPackage
                );

                // Handle loading and error states for packages
                if (isLoadingPackage) {
                  return (
                    <div className="flex items-center justify-center h-32 text-gray-500">
                      <div className="text-center">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-500 mx-auto mb-2"></div>
                        <div>Loading package details...</div>
                      </div>
                    </div>
                  );
                }

                if (!regularInventories.length && (!packageDetails?.isPackageData || !packageDetails?.campaignInventories?.length)) {
                  return (
                    <div className="flex items-center justify-center h-32 text-gray-500">
                      No inventory selected
                    </div>
                  );
                }

                return (
                  <div>
                    {/* Digital Inventory Section - Regular Campaign Only */}
                    {digitalInventory.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 px-2 py-2 mb-2 bg-blue-50 border-l-4 border-blue-500">
                          <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
                          <span className="text-sm font-semibold text-blue-700">Digital Inventory ({digitalInventory.length})</span>
                        </div>
                        {digitalInventory.map((item: any) => (
                          <div
                            key={item.originalIndex}
                            className={`group cursor-pointer p-2 mb-2 rounded-lg border transition-all duration-200 ${selectedInventoryIndex === item.originalIndex
                              ? 'bg-emerald-100 border-emerald-300 shadow-sm'
                              : 'bg-white border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50'
                              }`}
                            onClick={() => {
                              setSelectedInventoryIndex(item.originalIndex);
                              const mainImages = [
                                'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop&auto=format',
                                'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop&auto=format',
                                'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop&auto=format',
                                'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop&auto=format'
                              ];
                              setSelectedMainImage(mainImages[item.originalIndex % mainImages.length] || mainImages[0]);
                            }}
                          >
                            <div className="flex gap-3">
                              <img
                                src={item.originalData?.inventoryThumbnailUrl || (() => {
                                  const images = [
                                    'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=60&h=60&fit=crop&auto=format',
                                    'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=60&h=60&fit=crop&auto=format',
                                    'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=60&h=60&fit=crop&auto=format',
                                    'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=60&h=60&fit=crop&auto=format'
                                  ];
                                  return images[item.originalIndex % images.length] || images[0];
                                })()}
                                alt={item.name}
                                className="w-12 h-12 rounded-lg object-cover border border-emerald-200"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 text-sm truncate" title={item.name}>
                                  {item.name}
                                </div>
                                <div className="text-xs text-gray-500 mb-1 truncate" title={item.siteLocation}>{item.siteLocation}</div>
                                <div className="text-xs text-gray-500 mb-1 truncate" title={item.originalData?.mediaOwnerName}>By {item.originalData?.mediaOwnerName}</div>
                                <div className="flex flex-col gap-1">
                                  <Badge variant="outline" className={`text-xs px-2 py-0 h-5 ${getInventoryTypeColor("Digital").badge} border-current w-fit`}>
                                    {"Digital"}
                                  </Badge>
                                  <span className="text-xs text-gray-500 truncate" title={item.category || 'N/A'}>{item.category || 'N/A'}</span>
                                </div>
                              </div>

                              {selectedInventoryIndex === item.originalIndex && (
                                <div className="flex items-start">
                                  <div className="w-3 h-3 bg-emerald-500 rounded-full mt-1"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Classic Inventory Section - Regular Campaign Only */}
                    {classicInventory.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 px-2 py-2 mb-2 bg-green-50 border-l-4 border-green-500">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                          <span className="text-sm font-semibold text-green-700">Classic Inventory ({classicInventory.length})</span>
                        </div>
                        {classicInventory.map((item: any) => (
                          <div
                            key={item.originalIndex}
                            className={`group cursor-pointer p-2 mb-2 rounded-lg border transition-all duration-200 ${selectedInventoryIndex === item.originalIndex
                              ? 'bg-emerald-100 border-emerald-300 shadow-sm'
                              : 'bg-white border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50'
                              }`}
                            onClick={() => {
                              setSelectedInventoryIndex(item.originalIndex);
                              const mainImages = [
                                'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop&auto=format',
                                'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop&auto=format',
                                'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop&auto=format',
                                'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop&auto=format'
                              ];
                              setSelectedMainImage(mainImages[item.originalIndex % mainImages.length] || mainImages[0]);
                            }}
                          >
                            <div className="flex gap-3">
                              <img
                                src={item.originalData?.inventoryThumbnailUrl || (() => {
                                  const images = [
                                    'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=60&h=60&fit=crop&auto=format',
                                    'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=60&h=60&fit=crop&auto=format',
                                    'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=60&h=60&fit=crop&auto=format',
                                    'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=60&h=60&fit=crop&auto=format'
                                  ];
                                  return images[item.originalIndex % images.length] || images[0];
                                })()}
                                alt={item.name}
                                className="w-12 h-12 rounded-lg object-cover border border-emerald-200"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 text-sm truncate">{item.name}</div>
                                <div className="text-xs text-gray-500 mb-1 truncate">{item.siteLocation}</div>
                                <div className="flex flex-col gap-1">
                                  <Badge variant="outline" className={`text-xs px-2 py-0 h-5 ${getInventoryTypeColor("Classic").badge} border-current w-fit`}>
                                    {"Classic"}
                                  </Badge>
                                  <span className="text-xs text-gray-500 truncate" title={item.category || 'N/A'}>{item.category || 'N/A'}</span>
                                </div>
                              </div>

                              {selectedInventoryIndex === item.originalIndex && (
                                <div className="flex items-start">
                                  <div className="w-3 h-3 bg-emerald-500 rounded-full mt-1"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Package Inventory Section */}
                    {(packageInventory.length > 0 || packageError) && (
                      <div>
                        <div className="flex items-center gap-2 px-2 py-2 mb-2 bg-purple-50 border-l-4 border-purple-500">
                          <div className="w-2 h-2 bg-purple-500 rounded-full"></div>
                          <span className="text-sm font-semibold text-purple-700">
                            Package Inventory {packageInventory.length > 0 ? `(${packageInventory.length})` : ''}
                          </span>
                        </div>

                        {packageError ? (
                          <div className="flex items-center justify-center h-20 text-red-500 border border-red-200 rounded-lg bg-red-50 mx-2 mb-2">
                            <div className="text-center">
                              <div className="text-sm font-medium">Error loading packages:</div>
                              <div className="text-xs mt-1">{packageError}</div>
                            </div>
                          </div>
                        ) : (
                          <>
                        {/* Individual Package Items with Toggle */}
                        {packageInventory.map((packageItem: any) => {
                          const packageId = packageItem.originalData?.inventoryId || packageItem.originalData?.id || packageItem.id;
                          const isExpanded = expandedPackages.has(packageId);
                          const isLoading = loadingPackages.has(packageId);
                          const packageDetails = individualPackageDetails.get(packageId);

                          // Get sub-inventories if package is expanded and details are loaded
                          let packageDigitalInventory = [];
                          let packageClassicInventory = [];

                          if (isExpanded && packageDetails?.campaignInventories) {
                            const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];

                            packageDigitalInventory = packageDetails.campaignInventories
                              .filter((item: any) => item.inventoryType && item.inventoryType.toLowerCase().includes('digital'))
                              .map((item: any, index: number) => ({
                                ...item,
                                originalIndex: regularInventories.length + packageDetails.campaignInventories.indexOf(item),
                                originalData: item,
                                isFromPackage: true,
                                parentPackageId: packageId
                              }));

                            packageClassicInventory = packageDetails.campaignInventories
                              .filter((item: any) => item.inventoryType && (item.inventoryType.toLowerCase().includes('classic') || item.inventoryType.toLowerCase().includes('static')))
                              .map((item: any, index: number) => ({
                                ...item,
                                originalIndex: regularInventories.length + packageDetails.campaignInventories.indexOf(item),
                                originalData: item,
                                isFromPackage: true,
                                parentPackageId: packageId
                              }));
                          }

                          return (
                            <div key={packageId} className="mb-3">
                              {/* Package Header - Clickable to Toggle */}
                              <div
                                className={`group cursor-pointer p-3 rounded-lg border transition-all duration-200 ${selectedInventoryIndex === packageItem.originalIndex
                                    ? 'bg-emerald-100 border-emerald-300 shadow-sm'
                                    : 'bg-white border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50'
                                  } ${isExpanded ? 'border-purple-300 bg-purple-25' : ''}`}
                                  onClick={(e) => {
                                        e.stopPropagation(); // Prevent package selection when clicking toggle
                                        togglePackageExpansion(packageId, packageItem);
                                      }}
                              >
                                <div className="flex gap-3 items-center">
                                  <img
                                    src={packageItem.originalData?.inventoryThumbnailUrl || (() => {
                                      const images = [
                                        'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=60&h=60&fit=crop&auto=format',
                                        'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=60&h=60&fit=crop&auto=format',
                                        'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=60&h=60&fit=crop&auto=format',
                                        'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=60&h=60&fit=crop&auto=format'
                                      ];
                                      return images[packageItem.originalIndex % images.length] || images[0];
                                    })()}
                                    alt={packageItem.name}
                                    className="w-12 h-12 rounded-lg object-cover border border-emerald-200"
                                  />
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 text-sm truncate">{packageItem.name}</div>
                                    <div className="text-xs text-gray-500 mb-1 truncate">{packageItem.siteLocation}</div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="outline" className={`text-xs px-2 py-0 h-5 ${getInventoryTypeColor('Package').badge} border-current w-fit`}>
                                        Package
                                      </Badge>
                                      {isExpanded && packageDetails && (
                                        <span className="text-xs text-purple-600 font-medium">
                                          {packageDetails.campaignInventories?.length || 0} sub-billboards
                                        </span>
                                      )}
                                    </div>
                                  </div>

                                  {/* Toggle Button and Selection Indicator */}
                                  <div className="flex items-center gap-2">
                                    {selectedInventoryIndex === packageItem.originalIndex && (
                                      <div className="w-3 h-3 bg-emerald-500 rounded-full"></div>
                                    )}

                                    {/* Toggle Expand/Collapse Button */}
                                    <button
                                      className="p-1 rounded-full hover:bg-purple-100 transition-colors"
                                      onClick={(e) => {
                                        e.stopPropagation(); // Prevent package selection when clicking toggle
                                        togglePackageExpansion(packageId, packageItem);
                                      }}
                                      disabled={isLoading}
                                    >
                                      {isLoading ? (
                                        <div className="w-4 h-4 border-2 border-purple-300 border-t-purple-600 rounded-full animate-spin"></div>
                                      ) : (
                                        <ChevronDown
                                          className={`w-4 h-4 text-purple-600 transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''
                                            }`}
                                        />
                                      )}
                                    </button>
                                  </div>
                                </div>
                              </div>

                              {/* Package Sub-Inventories - Show when expanded */}
                              {isExpanded && packageDetails && (
                                <div className="ml-4 mt-2 border-l-2 border-purple-200 pl-3">
                                  {/* Package Digital Sub-Inventories */}
                                  {packageDigitalInventory.length > 0 && (
                                    <div className="mb-3">
                                      <div className="flex items-center gap-2 px-2 py-1 mb-2 bg-blue-25 border-l-2 border-blue-400">
                                        <div className="w-1.5 h-1.5 bg-blue-400 rounded-full"></div>
                                        <span className="text-xs font-medium text-blue-600">Digital ({packageDigitalInventory.length})</span>
                                      </div>
                                      {packageDigitalInventory.map((item: any) => (
                                        <div
                                          key={item.originalIndex}
                                          className={`group cursor-pointer p-2 mb-1 rounded border transition-all duration-200 ${selectedInventoryIndex === item.originalIndex
                                              ? 'bg-emerald-50 border-emerald-200 shadow-sm'
                                              : 'bg-white border-gray-200 hover:border-emerald-200 hover:bg-emerald-25'
                                            }`}
                                          onClick={() => {
                                            setSelectedInventoryIndex(item.originalIndex);
                                            const mainImages = [
                                              'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop&auto=format',
                                              'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop&auto=format',
                                              'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop&auto=format',
                                              'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop&auto=format'
                                            ];
                                            setSelectedMainImage(mainImages[(item.originalIndex - (selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || []).length) % mainImages.length] || mainImages[0]);
                                          }}
                                        >
                                          <div className="flex gap-2">
                                            <img
                                              src={item.inventoryThumbnailUrl || (() => {
                                                const images = [
                                                  'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=40&h=40&fit=crop&auto=format',
                                                  'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=40&h=40&fit=crop&auto=format',
                                                  'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=40&h=40&fit=crop&auto=format',
                                                  'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=40&h=40&fit=crop&auto=format'
                                                ];
                                                return images[(item.originalIndex - (selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || []).length) % images.length] || images[0];
                                              })()}
                                              alt={item.inventoryName}
                                              className="w-8 h-8 rounded object-cover border border-gray-200"
                                            />
                                            <div className="flex-1 min-w-0">
                                              <div className="font-medium text-gray-800 text-xs truncate" title={item.inventoryName}>
                                                {item.inventoryName}
                                              </div>
                                              <div className="text-xs text-gray-500 truncate" title={item.inventoryAddress}>
                                                {item.inventoryAddress}
                                              </div>
                                              <Badge variant="outline" className={`text-xs px-1 py-0 h-4 ${getInventoryTypeColor('Digital').badge} border-current w-fit mt-1`}>
                                                Digital
                                              </Badge>
                                            </div>

                                            {selectedInventoryIndex === item.originalIndex && (
                                              <div className="flex items-center">
                                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Package Classic Sub-Inventories */}
                                  {packageClassicInventory.length > 0 && (
                                    <div className="mb-3">
                                      <div className="flex items-center gap-2 px-2 py-1 mb-2 bg-green-25 border-l-2 border-green-400">
                                        <div className="w-1.5 h-1.5 bg-green-400 rounded-full"></div>
                                        <span className="text-xs font-medium text-green-600">Classic ({packageClassicInventory.length})</span>
                                      </div>
                                      {packageClassicInventory.map((item: any) => (
                                        <div
                                          key={item.originalIndex}
                                          className={`group cursor-pointer p-2 mb-1 rounded border transition-all duration-200 ${selectedInventoryIndex === item.originalIndex
                                              ? 'bg-emerald-50 border-emerald-200 shadow-sm'
                                              : 'bg-white border-gray-200 hover:border-emerald-200 hover:bg-emerald-25'
                                            }`}
                                          onClick={() => {
                                            setSelectedInventoryIndex(item.originalIndex);
                                            const mainImages = [
                                              'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop&auto=format',
                                              'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop&auto=format',
                                              'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop&auto=format',
                                              'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop&auto=format'
                                            ];
                                            setSelectedMainImage(mainImages[(item.originalIndex - (selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || []).length) % mainImages.length] || mainImages[0]);
                                          }}
                                        >
                                          <div className="flex gap-2">
                                            <img
                                              src={item.inventoryThumbnailUrl || (() => {
                                                const images = [
                                                  'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=40&h=40&fit=crop&auto=format',
                                                  'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=40&h=40&fit=crop&auto=format',
                                                  'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=40&h=40&fit=crop&auto=format',
                                                  'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=40&h=40&fit=crop&auto=format'
                                                ];
                                                return images[(item.originalIndex - (selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || []).length) % images.length] || images[0];
                                              })()}
                                              alt={item.inventoryName}
                                              className="w-8 h-8 rounded object-cover border border-gray-200"
                                            />
                                            <div className="flex-1 min-w-0">
                                              <div className="font-medium text-gray-800 text-xs truncate" title={item.inventoryName}>
                                                {item.inventoryName}
                                              </div>
                                              <div className="text-xs text-gray-500 truncate" title={item.inventoryAddress}>
                                                {item.inventoryAddress}
                                              </div>
                                              <Badge variant="outline" className={`text-xs px-1 py-0 h-4 ${getInventoryTypeColor('Classic').badge} border-current w-fit mt-1`}>
                                                Classic
                                              </Badge>
                                            </div>

                                            {selectedInventoryIndex === item.originalIndex && (
                                              <div className="flex items-center">
                                                <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                                              </div>
                                            )}
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  )}

                                  {/* Show message if no sub-inventories of any type */}
                                  {packageDigitalInventory.length === 0 && packageClassicInventory.length === 0 && (
                                    <div className="text-xs text-gray-500 italic p-2">
                                      No sub-billboards available for this package
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          );
                        })}
                          </>
                        )}
                      </div>
                    )}

                    {/* Deal Inventory Section */}
                    {dealInventory.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 px-2 py-2 mb-2 bg-emerald-50 border-l-4 border-emerald-500">
                          <div className="w-2 h-2 bg-emerald-500 rounded-full"></div>
                          <span className="text-sm font-semibold text-emerald-700">Deal Inventory ({dealInventory.length})</span>
                        </div>
                        {dealInventory.map((item: any) => (
                          <div
                            key={item.originalIndex}
                            className={`group cursor-pointer p-2 mb-2 rounded-lg border transition-all duration-200 ${selectedInventoryIndex === item.originalIndex
                              ? 'bg-emerald-100 border-emerald-300 shadow-sm'
                              : 'bg-white border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50'
                              }`}
                            onClick={() => {
                              setSelectedInventoryIndex(item.originalIndex);
                              const mainImages = [
                                'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop&auto=format',
                                'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop&auto=format',
                                'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop&auto=format',
                                'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop&auto=format'
                              ];
                              setSelectedMainImage(mainImages[item.originalIndex % mainImages.length] || mainImages[0]);
                            }}
                          >
                            <div className="flex gap-3">
                              <img
                                src={item.originalData?.inventoryThumbnailUrl || (() => {
                                  const images = [
                                    'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=60&h=60&fit=crop&auto=format',
                                    'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=60&h=60&fit=crop&auto=format',
                                    'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=60&h=60&fit=crop&auto=format',
                                    'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=60&h=60&fit=crop&auto=format'
                                  ];
                                  return images[item.originalIndex % images.length] || images[0];
                                })()}
                                alt={item.name}
                                className="w-12 h-12 rounded-lg object-cover border border-emerald-200"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 text-sm truncate">{item.name}</div>
                                <div className="text-xs text-gray-500 mb-1 truncate">{item.siteLocation}</div>
                                <div className="flex flex-col gap-1">
                                  <Badge variant="outline" className={`text-xs px-2 py-0 h-5 ${getInventoryTypeColor(item.type || "Deal").badge} border-current w-fit`}>
                                    {item.type || "Deal"}
                                  </Badge>
                                  <span className="text-xs text-gray-500 truncate" title={item.category || 'N/A'}>{item.category || 'N/A'}</span>
                                </div>
                              </div>

                              {selectedInventoryIndex === item.originalIndex && (
                                <div className="flex items-start">
                                  <div className="w-3 h-3 bg-emerald-500 rounded-full mt-1"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Other Inventory Types */}
                    {otherInventory.length > 0 && (
                      <div>
                        <div className="flex items-center gap-2 px-2 py-2 mb-2 bg-gray-50 border-l-4 border-gray-400">
                          <div className="w-2 h-2 bg-gray-400 rounded-full"></div>
                          <span className="text-sm font-semibold text-gray-600">Other Inventory ({otherInventory.length})</span>
                        </div>
                        {otherInventory.map((item: any) => (
                          <div
                            key={item.originalIndex}
                            className={`group cursor-pointer p-2 mb-2 rounded-lg border transition-all duration-200 ${selectedInventoryIndex === item.originalIndex
                              ? 'bg-emerald-100 border-emerald-300 shadow-sm'
                              : 'bg-white border-emerald-200 hover:border-emerald-300 hover:bg-emerald-50'
                              }`}
                            onClick={() => {
                              setSelectedInventoryIndex(item.originalIndex);
                              const mainImages = [
                                'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop&auto=format',
                                'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=600&fit=crop&auto=format',
                                'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=800&h=600&fit=crop&auto=format',
                                'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=800&h=600&fit=crop&auto=format'
                              ];
                              setSelectedMainImage(mainImages[item.originalIndex % mainImages.length] || mainImages[0]);
                            }}
                          >
                            <div className="flex gap-3">
                              <img
                                src={item.originalData?.inventoryThumbnailUrl || (() => {
                                  const images = [
                                    'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=60&h=60&fit=crop&auto=format',
                                    'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=60&h=60&fit=crop&auto=format',
                                    'https://images.unsplash.com/photo-1518709268805-4e9042af2176?w=60&h=60&fit=crop&auto=format',
                                    'https://images.unsplash.com/photo-1556742049-0cfed4f6a45d?w=60&h=60&fit=crop&auto=format'
                                  ];
                                  return images[item.originalIndex % images.length] || images[0];
                                })()}
                                alt={item.name}
                                className="w-12 h-12 rounded-lg object-cover border border-emerald-200"
                              />
                              <div className="flex-1 min-w-0">
                                <div className="font-medium text-gray-900 text-sm truncate">{item.name}</div>
                                <div className="text-xs text-gray-500 mb-1 truncate">{item.siteLocation}</div>
                                <div className="flex flex-col gap-1">
                                  <Badge variant="outline" className={`text-xs px-2 py-0 h-5 ${getInventoryTypeColor(item.type || "Other").badge} border-current w-fit`}>
                                    {item.type || ""}
                                  </Badge>
                                  <span className="text-xs text-gray-500 truncate" title={item.category || 'N/A'}>{item.category || 'N/A'}</span>
                                </div>
                              </div>

                              {selectedInventoryIndex === item.originalIndex && (
                                <div className="flex items-start">
                                  <div className="w-3 h-3 bg-emerald-500 rounded-full mt-1"></div>
                                </div>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })()}
              </div>
            </div>

            {/* Right Content Area */}
            <div className="flex-1 flex flex-col bg-white">
              {/* Tabs */}
              <div className="border-b border-emerald-200 bg-emerald-50/30">
                <div className="flex">
                  <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'map'
                      ? 'border-emerald-600 text-emerald-700 bg-white'
                      : 'border-transparent text-emerald-600 hover:text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50'
                      }`}
                    onClick={() => {
                      setActiveTab('map');
                      // Force map re-initialization when switching to map tab
                      setMapForceRender(prev => prev + 1);
                    }}
                  >
                    Map
                  </button>
                  <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'gallery'
                      ? 'border-emerald-600 text-emerald-700 bg-white'
                      : 'border-transparent text-emerald-600 hover:text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50'
                      }`}
                    onClick={() => setActiveTab('gallery')}
                  >
                    Gallery
                  </button>
                  {/* <button
                    className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${activeTab === 'streetview'
                      ? 'border-emerald-600 text-emerald-700 bg-white'
                      : 'border-transparent text-emerald-600 hover:text-emerald-700 hover:border-emerald-300 hover:bg-emerald-50'
                      }`}
                    onClick={() => setActiveTab('streetview')}
                  >
                    Street View
                  </button> */}
                </div>
              </div>

              {/* Content Area - Main content with scroll */}
              <div className="flex-1 overflow-hidden flex flex-col">
                {activeTab === 'gallery' && (
                  <div className="h-full flex flex-col">
                    {/* Scrollable Image Area */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ maxHeight: 'calc(100vh - 350px)' }}>
                      <div className="p-3">
                        <div className="w-full rounded overflow-hidden bg-gray-100" style={{ minHeight: '400px', height: '70vh' }}>
                          <img
                            src={(() => {
                              // Get current item using unified indexing approach
                              const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                              let currentItem;

                              if (selectedInventoryIndex < regularInventories.length) {
                                // Selected item is from regular campaign inventories
                                currentItem = regularInventories[selectedInventoryIndex];
                              } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                // Selected item is from package sub-inventories
                                const packageIndex = selectedInventoryIndex - regularInventories.length;
                                currentItem = packageDetails.campaignInventories[packageIndex];
                              }

                              // Use the actual thumbnail URL if available, otherwise fallback
                              return currentItem?.inventoryThumbnailUrl ||
                                selectedMainImage ||
                                'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop&auto=format';
                            })()}
                            alt="Billboard"
                            className="w-full h-full object-cover"
                            onError={(e) => {
                              e.currentTarget.src = 'https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=800&h=600&fit=crop&auto=format';
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    {/* Info Section at Bottom */}
                    <div className="flex-shrink-0 border-t bg-white">
                      {/* Unified Inventory Information Section */}
                      <div className="px-3 md:px-6 lg:px-8 py-3" style={{ minHeight: '120px' }}>
                        <div className="space-y-3 h-full">
                          {/* Inventory Details */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 lg:gap-8 text-sm">
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Location</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                // Get current item using unified indexing approach
                                const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                let currentItem;

                                if (selectedInventoryIndex < regularInventories.length) {
                                  // Selected item is from regular campaign inventories
                                  currentItem = regularInventories[selectedInventoryIndex];
                                } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                  // Selected item is from package sub-inventories
                                  const packageIndex = selectedInventoryIndex - regularInventories.length;
                                  currentItem = packageDetails.campaignInventories[packageIndex];
                                }
                                return currentItem?.inventoryAddress || currentItem?.siteLocation || 'N/A';
                              })()}>
                                {(() => {
                                  // Get current item using unified indexing approach
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  let currentItem;

                                  if (selectedInventoryIndex < regularInventories.length) {
                                    // Selected item is from regular campaign inventories
                                    currentItem = regularInventories[selectedInventoryIndex];
                                  } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    // Selected item is from package sub-inventories
                                    const packageIndex = selectedInventoryIndex - regularInventories.length;
                                    currentItem = packageDetails.campaignInventories[packageIndex];
                                  }
                                  return currentItem?.inventoryAddress || currentItem?.siteLocation || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Resolution</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                // Get current item using unified indexing approach
                                const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                let currentItem;

                                if (selectedInventoryIndex < regularInventories.length) {
                                  // Selected item is from regular campaign inventories
                                  currentItem = regularInventories[selectedInventoryIndex];
                                } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                  // Selected item is from package sub-inventories
                                  const packageIndex = selectedInventoryIndex - regularInventories.length;
                                  currentItem = packageDetails.campaignInventories[packageIndex];
                                }
                                return currentItem?.inventoryResolution || currentItem?.specifications?.resolution || 'N/A';
                              })()}>
                                {(() => {
                                  // Get current item using unified indexing approach
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  let currentItem;

                                  if (selectedInventoryIndex < regularInventories.length) {
                                    // Selected item is from regular campaign inventories
                                    currentItem = regularInventories[selectedInventoryIndex];
                                  } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    // Selected item is from package sub-inventories
                                    const packageIndex = selectedInventoryIndex - regularInventories.length;
                                    currentItem = packageDetails.campaignInventories[packageIndex];
                                  }
                                  return currentItem?.inventoryResolution || currentItem?.specifications?.resolution || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Category</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                // Get current item using unified indexing approach
                                const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                let currentItem;

                                if (selectedInventoryIndex < regularInventories.length) {
                                  // Selected item is from regular campaign inventories
                                  currentItem = regularInventories[selectedInventoryIndex];
                                } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                  // Selected item is from package sub-inventories
                                  const packageIndex = selectedInventoryIndex - regularInventories.length;
                                  currentItem = packageDetails.campaignInventories[packageIndex];
                                }
                                return currentItem?.inventoryVenueType || currentItem?.category || 'N/A';
                              })()}>
                                {(() => {
                                  // Get current item using unified indexing approach
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  let currentItem;

                                  if (selectedInventoryIndex < regularInventories.length) {
                                    // Selected item is from regular campaign inventories
                                    currentItem = regularInventories[selectedInventoryIndex];
                                  } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    // Selected item is from package sub-inventories
                                    const packageIndex = selectedInventoryIndex - regularInventories.length;
                                    currentItem = packageDetails.campaignInventories[packageIndex];
                                  }
                                  return currentItem?.inventoryVenueType || currentItem?.category || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Venue Type</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                // Get current item using unified indexing approach
                                const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                let currentItem;

                                if (selectedInventoryIndex < regularInventories.length) {
                                  // Selected item is from regular campaign inventories
                                  currentItem = regularInventories[selectedInventoryIndex];
                                } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                  // Selected item is from package sub-inventories
                                  const packageIndex = selectedInventoryIndex - regularInventories.length;
                                  currentItem = packageDetails.campaignInventories[packageIndex];
                                }
                                return currentItem?.inventoryVenueType || currentItem?.venueType || 'N/A';
                              })()}>
                                {(() => {
                                  // Get current item using unified indexing approach
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  let currentItem;

                                  if (selectedInventoryIndex < regularInventories.length) {
                                    // Selected item is from regular campaign inventories
                                    currentItem = regularInventories[selectedInventoryIndex];
                                  } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    // Selected item is from package sub-inventories
                                    const packageIndex = selectedInventoryIndex - regularInventories.length;
                                    currentItem = packageDetails.campaignInventories[packageIndex];
                                  }
                                  return currentItem?.inventoryVenueType || currentItem?.venueType || 'N/A';
                                })()}
                              </div>
                            </div>
                          </div>

                          {/* Additional Info Row */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 lg:gap-8 text-sm">
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Inventory Type</div>
                              <div className="font-medium text-gray-900 text-sm truncate">
                                {(() => {
                                  // Get current item using unified indexing approach
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  let currentItem;

                                  if (selectedInventoryIndex < regularInventories.length) {
                                    // Selected item is from regular campaign inventories
                                    currentItem = regularInventories[selectedInventoryIndex];
                                  } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    // Selected item is from package sub-inventories
                                    const packageIndex = selectedInventoryIndex - regularInventories.length;
                                    currentItem = packageDetails.campaignInventories[packageIndex];
                                  }

                                  const rawType = currentItem?.inventoryType || currentItem?.type || 'N/A';
                                  return _inventoryTypeDisplayMapping[rawType.toLowerCase()] || rawType;
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Reference ID</div>
                              <div className="font-medium text-gray-900 text-sm truncate">
                                {(() => {
                                  // Get current item using unified indexing approach
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  let currentItem;

                                  if (selectedInventoryIndex < regularInventories.length) {
                                    // Selected item is from regular campaign inventories
                                    currentItem = regularInventories[selectedInventoryIndex];
                                  } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    // Selected item is from package sub-inventories
                                    const packageIndex = selectedInventoryIndex - regularInventories.length;
                                    currentItem = packageDetails.campaignInventories[packageIndex];
                                  }
                                  return currentItem?.inventoryReferenceId || currentItem?.code || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Coordinates</div>
                              <div className="font-medium text-gray-900 text-sm truncate">
                                {(() => {
                                  // Get current item using unified indexing approach
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  let currentItem;

                                  if (selectedInventoryIndex < regularInventories.length) {
                                    // Selected item is from regular campaign inventories
                                    currentItem = regularInventories[selectedInventoryIndex];
                                  } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    // Selected item is from package sub-inventories
                                    const packageIndex = selectedInventoryIndex - regularInventories.length;
                                    currentItem = packageDetails.campaignInventories[packageIndex];
                                  }
                                  const lat = currentItem?.inventoryLatitude;
                                  const lng = currentItem?.inventoryLongitude;
                                  return (lat && lng) ? `${lat.toFixed(4)}, ${lng.toFixed(4)}` : 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Inventory Name</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                // Get current item using unified indexing approach
                                const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                let currentItem;

                                if (selectedInventoryIndex < regularInventories.length) {
                                  // Selected item is from regular campaign inventories
                                  currentItem = regularInventories[selectedInventoryIndex];
                                } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                  // Selected item is from package sub-inventories
                                  const packageIndex = selectedInventoryIndex - regularInventories.length;
                                  currentItem = packageDetails.campaignInventories[packageIndex];
                                }
                                return currentItem?.inventoryName || currentItem?.name || 'N/A';
                              })()}>
                                {(() => {
                                  // Get current item using unified indexing approach
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  let currentItem;

                                  if (selectedInventoryIndex < regularInventories.length) {
                                    // Selected item is from regular campaign inventories
                                    currentItem = regularInventories[selectedInventoryIndex];
                                  } else if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    // Selected item is from package sub-inventories
                                    const packageIndex = selectedInventoryIndex - regularInventories.length;
                                    currentItem = packageDetails.campaignInventories[packageIndex];
                                  }
                                  return currentItem?.inventoryName || currentItem?.name || 'N/A';
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Optional Schedule & Details Section */}
                      {/* <div className="border-t px-3 md:px-6 lg:px-8 py-3" style={{ minHeight: '100px' }}>
                        <div className="space-y-3 h-full">
                          <h4 className="text-lg font-semibold text-gray-900">Schedule & Details</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                            <div>
                              <div className="text-gray-600 text-sm font-medium mb-2">Schedule</div>
                              <div className="text-gray-900">
                                {(() => {
                                  // Check if scheduleData is available from the current item
                                  const inventoryData = packageDetails?.isPackageData 
                                    ? packageDetails.campaignInventories 
                                    : (selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || []);
                                  const currentItem = inventoryData[selectedInventoryIndex];
                                  const scheduleData = currentItem?.scheduleData;

                                  if (scheduleData && scheduleData.length > 0) {
                                    const firstSchedule = scheduleData[0];
                                    return `${firstSchedule.day}: ${firstSchedule.startTime} - ${firstSchedule.endTime}`;
                                  }
                                  return 'N/A';
                                })()}
                              </div>
                            </div>
                            <div>
                              <div className="text-gray-600 text-sm font-medium mb-2">Daypart</div>
                              <div className="text-gray-900">
                                {(() => {
                                  // Check if daypart is available from the current item
                                  const inventoryData = packageDetails?.isPackageData 
                                    ? packageDetails.campaignInventories 
                                    : (selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || []);
                                  const currentItem = inventoryData[selectedInventoryIndex];
                                  const daypart = currentItem?.daypart || currentItem?.timing?.daypart;
                                  return daypart || 'N/A';
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div> */}
                    </div>
                  </div>
                )}

                {activeTab === 'map' && (
                  <div className="h-full flex flex-col">
                    {/* Scrollable Map Area */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ maxHeight: 'calc(100vh - 350px)' }}>
                      {(() => {
                        // Get current item from unified inventory
                        const currentItem = unifiedInventory[selectedInventoryIndex];

                        // Check if current item has coordinates - be more flexible about coordinate location
                        const latitude = currentItem?.originalData?.inventoryLatitude ||
                          currentItem?.originalData?.latitude ||
                          currentItem?.latitude;
                        const longitude = currentItem?.originalData?.inventoryLongitude ||
                          currentItem?.originalData?.longitude ||
                          currentItem?.longitude;

                        const hasCoordinates = latitude && longitude &&
                          !isNaN(Number(latitude)) &&
                          !isNaN(Number(longitude)) &&
                          Number(latitude) !== 0 &&
                          Number(longitude) !== 0;

                        if (!hasCoordinates) {
                          return (
                            <div className="w-full bg-gray-100 flex items-center justify-center p-3" style={{ minHeight: '400px', height: '70vh' }}>
                              <div className="text-center text-gray-500">
                                <MapPin className="h-16 w-16 mx-auto mb-4 text-gray-400" />
                                <div className="text-xl font-medium mb-2">Map View</div>
                                <div className="text-sm text-gray-600 mb-4">
                                  Location coordinates not available for this inventory
                                </div>
                                <div className="text-xs text-gray-500">
                                  {currentItem?.originalData?.inventoryAddress || currentItem?.siteLocation || 'No address available'}
                                </div>
                              </div>
                            </div>
                          );
                        }

                        return (
                          <div className="w-full relative p-3" style={{ minHeight: '400px', height: '70vh' }}>
                            <div
                              ref={mapContainer}
                              className="w-full h-full rounded-none"
                            />
                            {!map && (
                              <div className="absolute inset-3 bg-gray-100 flex items-center justify-center rounded">
                                <div className="text-center text-gray-500">
                                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600 mx-auto mb-4"></div>
                                  <div className="text-sm">Loading map...</div>
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </div>

                    {/* Info Section at Bottom */}
                    <div className="flex-shrink-0 border-t bg-white">
                      {/* Unified Inventory Information Section */}
                      <div className="px-3 md:px-6 lg:px-8 py-3" style={{ minHeight: '120px' }}>
                        <div className="space-y-3 h-full">
                          {/* Inventory Details */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 lg:gap-8 text-sm">
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Location</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                // Get current item from unified inventory list
                                let allInventoryData = [];
                                const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                allInventoryData = [...regularInventories];
                                if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                  allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                }
                                const currentItem = allInventoryData[selectedInventoryIndex];
                                return currentItem?.inventoryAddress || currentItem?.siteLocation || 'N/A';
                              })()}>
                                {(() => {
                                  // Get current item from unified inventory list
                                  let allInventoryData = [];
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  allInventoryData = [...regularInventories];
                                  if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                  }
                                  const currentItem = allInventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryAddress || currentItem?.siteLocation || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Resolution</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                // Get current item from unified inventory list
                                let allInventoryData = [];
                                const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                allInventoryData = [...regularInventories];
                                if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                  allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                }
                                const currentItem = allInventoryData[selectedInventoryIndex];
                                return currentItem?.inventoryResolution || currentItem?.specifications?.resolution || 'N/A';
                              })()}>
                                {(() => {
                                  // Get current item from unified inventory list
                                  let allInventoryData = [];
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  allInventoryData = [...regularInventories];
                                  if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                  }
                                  const currentItem = allInventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryResolution || currentItem?.specifications?.resolution || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Category</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                // Get current item from unified inventory list
                                let allInventoryData = [];
                                const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                allInventoryData = [...regularInventories];
                                if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                  allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                }
                                const currentItem = allInventoryData[selectedInventoryIndex];
                                return currentItem?.inventoryVenueType || currentItem?.category || 'N/A';
                              })()}>
                                {(() => {
                                  // Get current item from unified inventory list
                                  let allInventoryData = [];
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  allInventoryData = [...regularInventories];
                                  if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                  }
                                  const currentItem = allInventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryVenueType || currentItem?.category || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Venue Type</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                // Get current item from unified inventory list
                                let allInventoryData = [];
                                const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                allInventoryData = [...regularInventories];
                                if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                  allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                }
                                const currentItem = allInventoryData[selectedInventoryIndex];
                                return currentItem?.inventoryVenueType || currentItem?.venueType || 'N/A';
                              })()}>
                                {(() => {
                                  // Get current item from unified inventory list
                                  let allInventoryData = [];
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  allInventoryData = [...regularInventories];
                                  if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                  }
                                  const currentItem = allInventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryVenueType || currentItem?.venueType || 'N/A';
                                })()}
                              </div>
                            </div>
                          </div>

                          {/* Additional Info Row */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 lg:gap-8 text-sm">
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Inventory Type</div>
                              <div className="font-medium text-gray-900 text-sm truncate">
                                {(() => {
                                  // Get current item from unified inventory list
                                  let allInventoryData = [];
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  allInventoryData = [...regularInventories];
                                  if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                  }
                                  const currentItem = allInventoryData[selectedInventoryIndex];

                                  const rawType = currentItem?.inventoryType || currentItem?.type || 'N/A';
                                  return _inventoryTypeDisplayMapping[rawType.toLowerCase()] || rawType;
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Reference ID</div>
                              <div className="font-medium text-gray-900 text-sm truncate">
                                {(() => {
                                  // Get current item from unified inventory list
                                  let allInventoryData = [];
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  allInventoryData = [...regularInventories];
                                  if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                  }
                                  const currentItem = allInventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryReferenceId || currentItem?.code || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Coordinates</div>
                              <div className="font-medium text-gray-900 text-sm truncate">
                                {(() => {
                                  // Get current item from unified inventory list
                                  let allInventoryData = [];
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  allInventoryData = [...regularInventories];
                                  if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                  }
                                  const currentItem = allInventoryData[selectedInventoryIndex];
                                  const lat = currentItem?.inventoryLatitude;
                                  const lng = currentItem?.inventoryLongitude;
                                  return (lat && lng) ? `${lat.toFixed(4)}, ${lng.toFixed(4)}` : 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Inventory Name</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                // Get current item from unified inventory list
                                let allInventoryData = [];
                                const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                allInventoryData = [...regularInventories];
                                if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                  allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                }
                                const currentItem = allInventoryData[selectedInventoryIndex];
                                return currentItem?.inventoryName || currentItem?.name || 'N/A';
                              })()}>
                                {(() => {
                                  // Get current item from unified inventory list
                                  let allInventoryData = [];
                                  const regularInventories = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  allInventoryData = [...regularInventories];
                                  if (packageDetails?.isPackageData && packageDetails.campaignInventories) {
                                    allInventoryData = [...allInventoryData, ...packageDetails.campaignInventories];
                                  }
                                  const currentItem = allInventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryName || currentItem?.name || 'N/A';
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {activeTab === 'streetview' && (
                  <div className="h-full flex flex-col">
                    {/* Scrollable Street View Area */}
                    <div className="flex-1 overflow-y-auto overflow-x-hidden" style={{ maxHeight: 'calc(100vh - 350px)' }}>
                      <div className="p-3" style={{ minHeight: '400px', height: '70vh' }}>
                        <div className="w-full h-full rounded overflow-hidden bg-gray-100 flex items-center justify-center">
                          {selectedCampaignBookings?.bookings?.length > 0 ? (
                            <div className="w-full h-full relative bg-gradient-to-br from-blue-50 to-emerald-50 flex items-center justify-center">
                              <div className="text-center">
                                <div className="w-32 h-32 mx-auto mb-4 bg-white rounded-full flex items-center justify-center border-4 border-emerald-200">
                                  <svg className="w-16 h-16 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25s-7.5-4.108-7.5-11.25a7.5 7.5 0 1115 0z" />
                                  </svg>
                                </div>
                                <h3 className="text-lg font-semibold text-gray-800 mb-2">Street View</h3>
                                <p className="text-sm text-gray-600 mb-4">
                                  360° street-level view of billboard location
                                </p>
                                <div className="inline-block px-4 py-2 bg-emerald-100 text-emerald-700 rounded-lg text-sm font-medium">
                                  Interactive view coming soon
                                </div>
                              </div>
                            </div>
                          ) : (
                            <div className="text-center text-gray-500">
                              <div className="w-16 h-16 mx-auto mb-4 opacity-50">
                                <svg fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25s-7.5-4.108-7.5-11.25a7.5 7.5 0 1115 0z" />
                                </svg>
                              </div>
                              <div className="text-lg font-medium mb-2">Street View</div>
                              <div className="text-sm">Select a billboard to view street perspective</div>
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Info Section at Bottom */}
                    <div className="flex-shrink-0 border-t bg-white">
                      {/* Unified Inventory Information Section */}
                      <div className="px-3 md:px-6 lg:px-8 py-3" style={{ minHeight: '120px' }}>
                        <div className="space-y-3 h-full">
                          {/* Inventory Details */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 lg:gap-8 text-sm">
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Location</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                const currentItem = inventoryData[selectedInventoryIndex];
                                return currentItem?.inventoryAddress || currentItem?.siteLocation || 'N/A';
                              })()}>
                                {(() => {
                                  const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  const currentItem = inventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryAddress || currentItem?.siteLocation || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Resolution</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                const currentItem = inventoryData[selectedInventoryIndex];
                                return currentItem?.inventoryResolution || currentItem?.specifications?.resolution || 'N/A';
                              })()}>
                                {(() => {
                                  const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  const currentItem = inventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryResolution || currentItem?.specifications?.resolution || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Category</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                const currentItem = inventoryData[selectedInventoryIndex];
                                return currentItem?.inventoryVenueType || currentItem?.category || 'N/A';
                              })()}>
                                {(() => {
                                  const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  const currentItem = inventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryVenueType || currentItem?.category || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Venue Type</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                const currentItem = inventoryData[selectedInventoryIndex];
                                return currentItem?.inventoryVenueType || currentItem?.venueType || 'N/A';
                              })()}>
                                {(() => {
                                  const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  const currentItem = inventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryVenueType || currentItem?.venueType || 'N/A';
                                })()}
                              </div>
                            </div>
                          </div>

                          {/* Additional Info Row */}
                          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 md:gap-6 lg:gap-8 text-sm">
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Inventory Type</div>
                              <div className="font-medium text-gray-900 text-sm truncate">
                                {(() => {
                                  const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  const currentItem = inventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryType || currentItem?.type || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Reference ID</div>
                              <div className="font-medium text-gray-900 text-sm truncate">
                                {(() => {
                                  const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  const currentItem = inventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryReferenceId || currentItem?.code || 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Coordinates</div>
                              <div className="font-medium text-gray-900 text-sm truncate">
                                {(() => {
                                  const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  const currentItem = inventoryData[selectedInventoryIndex];
                                  const lat = currentItem?.inventoryLatitude;
                                  const lng = currentItem?.inventoryLongitude;
                                  return (lat && lng) ? `${lat.toFixed(4)}, ${lng.toFixed(4)}` : 'N/A';
                                })()}
                              </div>
                            </div>
                            <div className="min-w-0">
                              <div className="text-gray-600 text-xs uppercase tracking-wide mb-1">Inventory Name</div>
                              <div className="font-medium text-gray-900 text-sm truncate" title={(() => {
                                const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                const currentItem = inventoryData[selectedInventoryIndex];
                                return currentItem?.inventoryName || currentItem?.name || 'N/A';
                              })()}>
                                {(() => {
                                  const inventoryData = selectedCampaignBookings?.campaignInventories || selectedCampaignBookings?.bookings || [];
                                  const currentItem = inventoryData[selectedInventoryIndex];
                                  return currentItem?.inventoryName || currentItem?.name || 'N/A';
                                })()}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Street View Information Section */}
                      <div className="border-t px-3 md:px-6 lg:px-8 py-3" style={{ minHeight: '100px' }}>
                        <div className="space-y-3 h-full">
                          <h4 className="text-lg font-semibold text-gray-900">Street View Details</h4>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-6">
                            <div>
                              <div className="text-gray-600 text-sm font-medium mb-2">Viewing Angle</div>
                              <div className="text-gray-900">N/A</div>
                            </div>
                            <div>
                              <div className="text-gray-600 text-sm font-medium mb-2">Distance from Road</div>
                              <div className="text-gray-900">N/A</div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Campaign Rejection Modal (Unified for both cancellation and rejection) */}
      <Dialog open={showRejectModal} onOpenChange={setShowRejectModal}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-red-600 flex items-center gap-2">
              <AlertTriangle className="h-5 w-5" />
              {rejectionType === "buyer_cancel" ? "Cancel Campaign" : "Reject Campaign"}
            </DialogTitle>
            <DialogDescription>
              You are about to {rejectionType === "buyer_cancel" ? "cancel" : "reject"} "{selectedCampaignToReject?.name}". This action cannot be undone.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            {selectedCampaignToReject && (
              <div className="bg-gray-50 p-4 rounded-lg">
                <div className="text-sm text-gray-600">
                  <p><strong>Campaign:</strong> {selectedCampaignToReject.name}</p>
                  <p><strong>Duration:</strong> {selectedCampaignToReject.duration}</p>
                  <p><strong>Total Cost:</strong> {formatCurrency(selectedCampaignToReject.totalPrice, selectedCampaignToReject?.priceSummary?.currency?.code)}</p>
                  {rejectionType === "buyer_cancel" && (
                    <p><strong>Refund Eligibility:</strong>
                      <span className={`ml-2 px-2 py-1 rounded text-xs ${canRejectCampaign(selectedCampaignToReject)
                        ? 'bg-green-100 text-green-800'
                        : 'bg-red-100 text-red-800'
                        }`}>
                        {canRejectCampaign(selectedCampaignToReject) ? 'Full Refund Available' : 'No Refund - Within 48 Hours'}
                      </span>
                    </p>
                  )}
                </div>
              </div>
            )}

            <div className="space-y-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  {rejectionType === "buyer_cancel" ? "Cancellation" : "Rejection"} Reason *
                </label>
                <Select value={rejectionReason} onValueChange={setRejectionReason}>
                  <SelectTrigger>
                    <SelectValue placeholder={`Select a reason for ${rejectionType === "buyer_cancel" ? "cancellation" : "rejection"}`} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="change_of_plans">Change of Plans</SelectItem>
                    <SelectItem value="budget_constraints">Budget Constraints</SelectItem>
                    <SelectItem value="campaign_postponed">Campaign Postponed</SelectItem>
                    <SelectItem value="duplicate_booking">Duplicate Booking</SelectItem>
                    <SelectItem value="technical_issues">Technical Issues</SelectItem>
                    <SelectItem value="content_issues">Content Issues</SelectItem>
                    <SelectItem value="scheduling_conflict">Scheduling Conflict</SelectItem>
                    <SelectItem value="other">Other</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Additional Details (Optional)
                </label>
                <Textarea
                  placeholder={`Provide additional details about the ${rejectionType === "buyer_cancel" ? "cancellation" : "rejection"}...`}
                  value={rejectionReasonDetails}
                  onChange={(e) => setRejectionReasonDetails(e.target.value)}
                  rows={3}
                />
              </div>
            </div>

            <div className="flex space-x-3 pt-4">
              <Button
                onClick={handleRejectCampaign}
                disabled={!rejectionReason || isProcessingRejection}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              >
                {isProcessingRejection ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Processing...
                  </>
                ) : (
                  <>
                    <XCircle className="h-4 w-4 mr-2" />
                    {rejectionType === "buyer_cancel" ? "Confirm Cancellation" : "Confirm Rejection"}
                  </>
                )}
              </Button>

              <Button
                onClick={() => setShowRejectModal(false)}
                variant="outline"
                disabled={isProcessingRejection}
                className="flex-1"
              >
                Keep Campaign
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Rejection Reason Display Modal */}
      <Dialog open={showRejectionReasonModal} onOpenChange={setShowRejectionReasonModal}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertCircle className="h-5 w-5" />
              Campaign Rejection Reason
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            <p className="text-sm text-gray-700 leading-relaxed">
              {selectedRejectionReason}
            </p>
          </div>
          <div className="flex justify-end mt-6">
            <Button onClick={() => setShowRejectionReasonModal(false)} variant="outline">
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Cart Conflict Modal */}
      <Dialog open={showCartConflictModal} onOpenChange={setShowCartConflictModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-emerald-600">Cart Contains Inventories</DialogTitle>
            <DialogDescription>
              Your cart contains inventories. Please clear the cart to continue with your draft campaign.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-3">
              <Button
                onClick={handleClearCartAndContinue}
                className="w-full bg-emerald-600 hover:bg-emerald-700 text-white"
              >
                ✅ Clear Cart & Continue
              </Button>

              <Button
                onClick={handleKeepCart}
                variant="outline"
                className="w-full border-red-600 text-red-600 hover:bg-red-50"
              >
                ❌ Keep Cart
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete All Modal for Cart Clear (API) */}
      <Dialog open={showDeleteAllModal} onOpenChange={setShowDeleteAllModal}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="text-red-600">Clear Cart</DialogTitle>
            <DialogDescription>
              Are you sure you want to remove all items from your cart?
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <Button
              onClick={confirmDeleteAll}
              className="w-full bg-red-600 hover:bg-red-700 text-white"
            >
              Confirm Clear Cart
            </Button>
            <Button
              onClick={() => setShowDeleteAllModal(false)}
              variant="outline"
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}